import React, { useState, useEffect, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faMoneyBillWave,
    faSync,
    faCheck,
    faLock,
    faFileExport,
    faPlus,
    faExclamationTriangle,
    faSpinner,
    faTimes,
    faDownload,
    faUser,
    faIdCard,
    faCalendarAlt,
    faClock,
    faCheckCircle,
    faTimesCircle,
    faUndo,
    faCommentDots,
    faChevronDown,
    faChevronUp,
    faQuestionCircle,
    faBan,
    faUndoAlt,
    faInfoCircle,
    faSnowflake,
    faPercent,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';
import AdjustmentDetailModal from '../../components/AdjustmentDetailModal';
import SearchableSelect from '../../components/SearchableSelect';

// Helper to normalize detail.surveys (old batches store as object, new as array)
const getSurveysArray = (line) => {
    const surveys = line?.detail?.surveys;
    if (!surveys) return [];
    if (Array.isArray(surveys)) return surveys;
    return Object.values(surveys);
};

// Helper to calculate original quantity and total — shows actual productivity
// (completes + carryover), NOT inflated by deductions from previous batches.
// Deductions are displayed separately in the adjustments column.
const getOriginalValues = (line) => {
    // Use subtotals if available (CATI lines)
    const subtotals = line.detail?.subtotals;
    if (subtotals) {
        const perComplete = parseFloat(subtotals.per_complete?.qty || 0);
        const perDay = parseFloat(subtotals.per_day?.qty || 0);
        const perCompleteAmt = parseFloat(subtotals.per_complete?.amount || 0);
        const perDayAmt = parseFloat(subtotals.per_day?.amount || 0);
        return {
            originalQty: perComplete + perDay,
            originalTotal: perCompleteAmt + perDayAmt,
        };
    }
    // Fallback: compute from detail.surveys (works for F2F and other lines)
    // Includes carryover (it's real work counted in this batch)
    const surveys = getSurveysArray(line).filter(s => !s.is_carryover_only);
    if (surveys.length > 0) {
        const totalQty = surveys.reduce((sum, s) => sum + (parseFloat(s.qty) || 0), 0);
        const totalAmount = surveys.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
        return { originalQty: totalQty, originalTotal: totalAmount };
    }
    return {
        originalQty: parseFloat(line.qty) || 0,
        originalTotal: parseFloat(line.total) || 0,
    };
};

// Format per-day qty as fraction when fractional (e.g. 1/6 jr instead of 0.1667 jour)
const fmtDayQty = (qty) => {
    if (qty === 0) return '0 jr';
    if (Number.isInteger(qty)) return `${qty} jour${qty > 1 ? 's' : ''}`;
    if (Math.abs(qty - 0.5) < 0.0001) return '½ jour';
    for (const numerator of [1, 0.5]) {
        const denom = Math.round(numerator / qty);
        if (denom > 1 && Math.abs(numerator / denom - qty) < 0.0001) {
            return `${numerator === 0.5 ? '½' : '1'}/${denom} jr`;
        }
    }
    return `${parseFloat(qty.toFixed(2))} jour${qty > 1 ? 's' : ''}`;
};

const PaymentBatchDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    // Admin viewers (administrateur_general, business officer) have read-only access — no action buttons
    const isAdminViewer = ['administrateur_general', 'administrateur_general_superieur'].includes(user?.role);
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState(false);
    const [openSections, setOpenSections] = useState({ cati: false, f2f: false, controller: false });

    // Modals
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [selectedLine, setSelectedLine] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showAdjustmentDetailModal, setShowAdjustmentDetailModal] = useState(false);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionNote, setRejectionNote] = useState('');
    const [showRejectionHistory, setShowRejectionHistory] = useState(user?.role !== 'administrateur_general');

    // Completion data
    const [completionData, setCompletionData] = useState(null);
    const [loadingCompletion, setLoadingCompletion] = useState(false);
    const [exportingCompletion, setExportingCompletion] = useState(false);

    const linesTableRef = useRef(null);
    const autoOpenedSectionRef = useRef(null);
    const completionSectionRef = useRef(null);
    const [searchParams] = useSearchParams();
    const focusCompletionDoneRef = useRef(false);

    // When a batch has lines in only one section (e.g. a frais-déplacement express
    // batch where only F2F is present), open that section by default. Done once per
    // batch so it doesn't fight a manual close.
    useEffect(() => {
        if (!batch || !Array.isArray(batch.lines)) return;
        if (autoOpenedSectionRef.current === batch.id) return;
        const sectionsWithLines = ['cati', 'f2f', 'controller'].filter(
            key => batch.lines.some(l => l.section === key)
        );
        if (sectionsWithLines.length === 1) {
            const only = sectionsWithLines[0];
            setOpenSections({ cati: false, f2f: false, controller: false, [only]: true });
            autoOpenedSectionRef.current = batch.id;
        }
    }, [batch]);

    // Section validation
    const [sectionProcessing, setSectionProcessing] = useState(false);
    const [showSectionRejectModal, setShowSectionRejectModal] = useState(false);
    const [sectionRejectTarget, setSectionRejectTarget] = useState(null);
    const [sectionRejectNotes, setSectionRejectNotes] = useState('');

    // Exclusion
    const [showExclusionModal, setShowExclusionModal] = useState(false);
    const [exclusionTarget, setExclusionTarget] = useState(null); // { line, surveyId? }
    const [exclusionReason, setExclusionReason] = useState('');
    const [exclusionProcessing, setExclusionProcessing] = useState(false);

    // Synthesis (admin generale)
    const [synthesisData, setSynthesisData] = useState(null);
    const [loadingSynthesis, setLoadingSynthesis] = useState(false);
    const [showSynthesis, setShowSynthesis] = useState(true);
    const [showCompletionTable, setShowCompletionTable] = useState(false); // hidden until clicked

    // Arriving from a cancellation notification (?focus=completion): auto-open the collapsed
    // "Completion des Paiements" section and scroll to it so the user lands straight on it.
    useEffect(() => {
        if (focusCompletionDoneRef.current) return;
        if (searchParams.get('focus') !== 'completion') return;
        if (!completionData || !completionData.lines || completionData.lines.length === 0) return;
        focusCompletionDoneRef.current = true;
        setShowCompletionTable(true);
        setTimeout(() => {
            if (completionSectionRef.current) {
                completionSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);
    }, [searchParams, completionData]);
    const [showExports, setShowExports] = useState(true);
    const [noteModal, setNoteModal] = useState(null); // full note text shown in a modal
    const collapseInitRef = useRef(false);
    const [expandedSynthesisSections, setExpandedSynthesisSections] = useState({});
    const [montantBreakdown, setMontantBreakdown] = useState(null); // survey object for breakdown popup
    const [excludedBreakdown, setExcludedBreakdown] = useState(null); // survey object for exclusion detail popup
    // Read-only popups: backdrop still closes, but only on a genuine click on it.
    const noteDismiss = useModalDismiss(() => setNoteModal(null), { enabled: !!noteModal });
    const montantDismiss = useModalDismiss(() => setMontantBreakdown(null), { enabled: !!montantBreakdown });
    const excludedDismiss = useModalDismiss(() => setExcludedBreakdown(null), { enabled: !!excludedBreakdown });
    const [adjustmentBreakdown, setAdjustmentBreakdown] = useState(null); // survey/task object for adjustment detail popup

    // Adjustment form
    const [adjustmentForm, setAdjustmentForm] = useState({
        type: 'bonus',
        amount: '',
        reason: '',
        survey_id: '',
    });
    const [allSurveys, setAllSurveys] = useState([]);
    const [loadingSurveys, setLoadingSurveys] = useState(false);
    const [interviewerBreakdownLine, setInterviewerBreakdownLine] = useState(null);
    // Le CT saisit ici les soumissions non contrôlées : un clic sur le fond ne doit
    // pas fermer et perdre la saisie. Sortie par ✕, Fermer ou Échap uniquement.
    const interviewerBreakdownDismiss = useModalDismiss(
        () => setInterviewerBreakdownLine(null),
        { backdropClose: false, enabled: !!interviewerBreakdownLine });
    const [f2fChoiceInputs, setF2fChoiceInputs] = useState({}); // { `${lineId}_${surveyId}`: draft value }

    useEffect(() => {
        fetchBatchDetails();
    }, [id]);

    // Default collapse states based on the batch status (set once on first load, so a
    // user's later toggles aren't overridden on refetch): exports collapse when confirmed,
    // rejection history collapses when validated.
    useEffect(() => {
        if (!batch || collapseInitRef.current) return;
        collapseInitRef.current = true;
        if (batch.status === 'confirmed') setShowExports(false);
        if (batch.status === 'validated') setShowRejectionHistory(false);
    }, [batch]);

    // Fetch completion data when batch is confirmed or has any confirmed line (includes excluded/partial after confirm)
    useEffect(() => {
        if (!batch || !id) return;
        const hasConfirmedLines = batch.lines && batch.lines.some(line => line.confirmed_at);
        const isConfirmedBatch = batch.status === 'confirmed';
        if (hasConfirmedLines || isConfirmedBatch) {
            fetchCompletionData();
        }
    }, [batch, id]);

    // Fetch synthesis data for authorized users on locked/validated/exported/confirmed batches
    useEffect(() => {
        if (!batch || !id) return;
        const adminRoles = ['administrateur_general', 'administrateur_general_superieur', 'resp_admin', 'chef_terrain_superieur'];
        const sectionHeadRoles = ['resp_cati', 'chef_terrain', 'resp_controle'];
        const canSeeSynthesis = (adminRoles.includes(user?.role) || sectionHeadRoles.includes(user?.role) || batch.is_delegated_to_me)
            && ['locked', 'validated', 'exported', 'confirmed'].includes(batch.status);
        if (canSeeSynthesis) {
            fetchSynthesis();
        }
    }, [batch?.id, batch?.status]);

    const fetchSynthesis = async (refresh = false) => {
        try {
            setLoadingSynthesis(true);
            const url = `/payments/${id}/synthesis${refresh ? '?refresh=1' : ''}`;
            const response = await api.get(url);
            if (response.data.success) {
                setSynthesisData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching synthesis:', err);
        } finally {
            setLoadingSynthesis(false);
        }
    };

    const toggleSynthesisSection = (section) => {
        setExpandedSynthesisSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getBudgetColor = (percentage) => {
        if (percentage >= 90) return { bar: 'bg-red-500', text: 'text-red-700' };
        if (percentage >= 75) return { bar: 'bg-yellow-500', text: 'text-yellow-700' };
        return { bar: 'bg-green-500', text: 'text-green-700' };
    };

    const fetchBatchDetails = async () => {
        try {
            setLoading(true);
            console.log('Fetching payment batch details for ID:', id);
            const response = await api.get(`/payments/${id}`);
            console.log('Payment batch API response:', response.data);
            if (response.data.success) {
                console.log('Payment batch data:', response.data.data);
                setBatch(response.data.data);
            } else {
                console.error('API returned success: false', response.data);
                setError(response.data.message || 'Erreur lors du chargement du lot de paiement');
            }
        } catch (err) {
            console.error('Error fetching batch details:', err);
            console.error('Error response:', err.response?.data);
            console.error('Error status:', err.response?.status);
            
            // Handle 403 Forbidden specifically
            if (err.response?.status === 403) {
                setError('Vous n\'avez pas accès à ce lot de paiement. Seuls les administrateurs et les utilisateurs délégués peuvent voir ce lot.');
            } else {
                setError(err.response?.data?.message || 'Erreur lors du chargement du lot de paiement');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRecalculate = async () => {
        setConfirmAction({
            title: 'Recalculer le lot',
            message: 'Êtes-vous sûr de vouloir recalculer ce lot ? Toutes les lignes seront régénérées à partir des données actuelles.',
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/recalculate`);
                    if (response.data.success) {
                        setSuccessMessage('Lot recalculé avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error recalculating batch:', err);
                    setError(err.response?.data?.message || 'Erreur lors du recalcul');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleRecalculateSection = async () => {
        setConfirmAction({
            title: 'Recalculer ma section',
            message: 'Êtes-vous sûr de vouloir recalculer votre section ? Tous les ajustements (bonus/pénalités) que vous avez ajoutés seront supprimés.',
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/recalculate-section`);
                    if (response.data.success) {
                        setSuccessMessage('Ajustements de votre section réinitialisés avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error recalculating section:', err);
                    setError(err.response?.data?.message || 'Erreur lors du recalcul de la section');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleToggleSectionSolde = async () => {
        try {
            setProcessing(true);
            const response = await api.post(`/payments/${id}/toggle-section-solde`);
            if (response.data.success) {
                setSuccessMessage(response.data.message);
                setShowSuccessModal(true);
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error toggling section Soldé:', err);
            setError(err.response?.data?.message || 'Erreur lors de la modification du mode "Soldé"');
        } finally {
            setProcessing(false);
        }
    };

    const handleLock = async () => {
        setConfirmAction({
            title: 'Verrouiller pour vérification',
            message: 'Êtes-vous sûr de vouloir verrouiller ce lot pour vérification ? Les montants seront figés et ne pourront plus être modifiés.',
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/lock`);
                    if (response.data.success) {
                        setSuccessMessage('Lot verrouillé pour vérification');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error locking batch:', err);
                    setError(err.response?.data?.message || 'Erreur lors du verrouillage');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleValidate = async () => {
        setConfirmAction({
            title: 'Valider le lot',
            message: 'Êtes-vous sûr de vouloir valider ce lot ? Cette action confirme que la vérification est terminée et approuve les montants.',
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/validate`);
                    if (response.data.success) {
                        setSuccessMessage('Lot validé avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error validating batch:', err);
                    setError(err.response?.data?.message || 'Erreur lors de la validation');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleReject = async () => {
        try {
            setProcessing(true);
            const response = await api.post(`/payments/${id}/reject`, {
                note: rejectionNote || ''
            });
            
            if (response.data.success) {
                setSuccessMessage('Lot retourné pour révision avec succès');
                setShowSuccessModal(true);
                setShowRejectModal(false);
                setRejectionNote('');
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error rejecting batch:', err);
            setError(err.response?.data?.message || 'Erreur lors du retour du lot');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleSolde = async () => {
        try {
            setProcessing(true);
            const response = await api.post(`/payments/${id}/toggle-solde`);
            if (response.data.success) {
                const isNowActive = response.data.data?.force_include_low_amounts ?? false;
                setSuccessMessage(
                    isNowActive 
                        ? 'Mode "Soldé" activé - Les montants < 200 MAD seront inclus dans les exports'
                        : 'Mode "Soldé" désactivé - Les montants < 200 MAD seront exclus des exports'
                );
                setShowSuccessModal(true);
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error toggling Soldé:', err);
            setError(err.response?.data?.message || 'Erreur lors de la modification du mode "Soldé"');
        } finally {
            setProcessing(false);
        }
    };

    const handleExport = async () => {
        setConfirmAction({
            title: 'Exporter le lot',
            message: 'Êtes-vous sûr de vouloir exporter ce lot ? Des fichiers Excel/CSV seront générés par méthode de paiement.',
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/export`);
                    if (response.data.success) {
                        setSuccessMessage(`Lot exporté avec succès ! ${response.data.data.exports.length} fichier(s) généré(s).`);
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error exporting batch:', err);
                    setError(err.response?.data?.message || 'Erreur lors de l\'exportation');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const getUserSection = () => {
        const sectionMap = {
            resp_cati: 'cati',
            chef_terrain: 'f2f',
            resp_controle: 'controller',
        };
        return sectionMap[user?.role] || null;
    };

    const handleValidateSection = async () => {
        const section = getUserSection();
        if (!section) return;

        setConfirmAction({
            title: 'Valider la section',
            message: `Confirmez-vous la validation de la section ${section === 'cati' ? 'CATI' : section === 'f2f' ? 'F2F' : 'Contrôleurs'} ? Cette action indique que vous avez vérifié les lignes de votre section.`,
            action: async () => {
                try {
                    setSectionProcessing(true);
                    const response = await api.post(`/payments/${id}/validate-section`, { section });
                    if (response.data.success) {
                        setSuccessMessage('Section validée avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error validating section:', err);
                    setError(err.response?.data?.message || 'Erreur lors de la validation de la section');
                } finally {
                    setSectionProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleOpenSectionReject = (section) => {
        setSectionRejectTarget(section);
        setSectionRejectNotes('');
        setShowSectionRejectModal(true);
    };

    const handleSubmitSectionReject = async () => {
        if (!sectionRejectTarget) return;

        try {
            setSectionProcessing(true);
            const response = await api.post(`/payments/${id}/reject-section`, {
                section: sectionRejectTarget,
                notes: sectionRejectNotes || '',
            });
            if (response.data.success) {
                const labels = { cati: 'CATI', f2f: 'F2F', controller: 'Contrôleurs' };
                setSuccessMessage(`Section ${labels[sectionRejectTarget]} rejetée`);
                setShowSuccessModal(true);
                setShowSectionRejectModal(false);
                setSectionRejectTarget(null);
                setSectionRejectNotes('');
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error rejecting section:', err);
            setError(err.response?.data?.message || 'Erreur lors du rejet de la section');
        } finally {
            setSectionProcessing(false);
        }
    };

    const handleAddAdjustment = (line) => {
        setSelectedLine(line);
        setAdjustmentForm({
            type: 'bonus',
            amount: '',
            reason: '',
            survey_id: '',
        });
        setShowAdjustmentModal(true);
        // Fetch all active surveys for the searchable dropdown
        // Uses /cati/surveys which is accessible to resp_cati, chef_terrain, and admin roles
        // The endpoint applies role-based filtering (CATI surveys for resp_cati, F2F for chef_terrain)
        if (allSurveys.length === 0) {
            setLoadingSurveys(true);
            api.get('/cati/surveys', { params: { active_only: true } })
                .then(res => {
                    const surveys = res.data?.data || res.data || [];
                    setAllSurveys(Array.isArray(surveys) ? surveys : []);
                })
                .catch(err => console.error('Error fetching surveys:', err))
                .finally(() => setLoadingSurveys(false));
        }
    };

    const handleSubmitAdjustment = async (e) => {
        e.preventDefault();
        
        try {
            setProcessing(true);
            const response = await api.post(
                `/payments/${id}/lines/${selectedLine.id}/adjustment`,
                adjustmentForm
            );
            
            if (response.data.success) {
                setSuccessMessage('Ajustement ajouté avec succès');
                setShowSuccessModal(true);
                setShowAdjustmentModal(false);
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error adding adjustment:', err);
            setError(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'ajustement');
        } finally {
            setProcessing(false);
        }
    };

    // F2F controlled-payment: chef terrain sets how many uncontrolled submissions to pay
    const handleSetF2fChoice = async (line, surveyId, value) => {
        try {
            setProcessing(true);
            const resp = await api.post(
                `/payments/${id}/lines/${line.id}/f2f-uncontrolled-choice`,
                { survey_id: surveyId, uncontrolled_to_pay: Math.max(0, parseInt(value, 10) || 0) }
            );
            if (resp.data.success) {
                const fresh = await api.get(`/payments/${id}`);
                if (fresh.data.success) {
                    setBatch(fresh.data.data);
                    const updated = (fresh.data.data.lines || []).find(l => l.id === line.id);
                    if (updated) setInterviewerBreakdownLine(updated);
                }
            }
        } catch (err) {
            console.error('Error setting F2F choice:', err);
            setError(err.response?.data?.message || 'Erreur lors de la mise à jour du choix de paiement');
        } finally {
            setProcessing(false);
        }
    };

    const handleForcePayment = async (line) => {
        setConfirmAction({
            title: 'Forcer le paiement',
            message: `Êtes-vous sûr de vouloir forcer le paiement pour ${line.person_name} (${line.net} MAD) ? Cette action ignorera la limite de 50 MAD pour les transferts.`,
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/lines/${line.id}/force-payment`);
                    if (response.data.success) {
                        setSuccessMessage('Paiement forcé avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error forcing payment:', err);
                    setError(err.response?.data?.message || 'Erreur lors du forçage du paiement');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    // Exclusion handlers
    const openExcludeLineModal = (line) => {
        setExclusionTarget({ line, surveyId: null });
        setExclusionReason('');
        setExclusionModalError('');
        setShowExclusionModal(true);
    };

    const openExcludeSurveyModal = (line, surveyId) => {
        setExclusionTarget({ line, surveyId });
        setExclusionReason('');
        setExclusionModalError('');
        setShowExclusionModal(true);
    };

    // "Geler": same modal as the exclusion, different endpoint/wording
    const openFreezeLineModal = (line) => {
        setExclusionTarget({ line, surveyId: null, mode: 'freeze' });
        setExclusionReason('');
        setExclusionModalError('');
        setShowExclusionModal(true);
    };

    // "Retenue" (F2F): withhold X % of one survey of the line — same modal, with a percentage
    const [retenuePercent, setRetenuePercent] = useState('');
    // Error shown INSIDE the exclusion/retenue/freeze modal (the page-level error is hidden behind it)
    const [exclusionModalError, setExclusionModalError] = useState('');
    const openRetenueModal = (line, surveyId) => {
        setExclusionTarget({ line, surveyId, mode: 'retenue' });
        setExclusionReason('');
        setRetenuePercent('');
        setExclusionModalError('');
        setShowExclusionModal(true);
    };
    // Retenue de garantie: only on a survey with work this week; the % applies on the survey's amount
    // minus old (released) retenue money — same rule as the backend.
    const surveyHasWork = (s) => !!s && !s.is_carryover_only && (parseFloat(s.qty || 0) - parseFloat(s.carryover_qty || 0)) > 0;
    const retenueBase = (s) => Math.max(0, parseFloat(s?.amount || 0) - parseFloat(s?.retenue_release_amount || 0));
    // Amount a retenue would withhold, and whether it fits in the line's net (backend refuses otherwise)
    const getRetenuePreview = (target, pctInput) => {
        if (!target || target.mode !== 'retenue') return null;
        const survey = getSurveysArray(target.line).find(s => String(s.survey_id) === String(target.surveyId));
        const base = retenueBase(survey);
        const pct = parseFloat(String(pctInput).replace(',', '.'));
        const valid = pct >= 1 && pct < 100;
        const withheld = valid ? Math.round(base * pct) / 100 : 0;
        const lineNet = parseFloat(target.line.net || 0);
        return { survey, base, pct, valid, withheld, lineNet, exceedsNet: valid && withheld > lineNet + 0.001 };
    };

    const postSurveyExclusion = async (line, surveyId, reason, percentage = null) => {
        const payload = { survey_id: surveyId, reason };
        if (percentage !== null) payload.percentage = percentage;
        return api.post(`/payments/${id}/lines/${line.id}/exclude-survey`, payload);
    };

    const handleExcludeLine = async () => {
        if (!exclusionTarget || !exclusionReason.trim()) return;
        setExclusionModalError('');

        if (exclusionTarget.mode === 'retenue') {
            const pct = parseFloat(String(retenuePercent).replace(',', '.'));
            if (!(pct >= 1 && pct <= 100)) {
                setExclusionModalError('Le pourcentage de retenue doit être compris entre 1 et 100');
                return;
            }
            const preview = getRetenuePreview(exclusionTarget, retenuePercent);
            if (preview?.exceedsNet) {
                setExclusionModalError(`Retenue impossible : ${preview.withheld.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD dépasserait le montant net de la ligne (${preview.lineNet.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD).`);
                return;
            }
            const { line, surveyId } = exclusionTarget;
            const reason = exclusionReason;
            if (pct >= 100) {
                // 100 % is not a retenue: offer the existing full exclusion of the survey instead
                const survey = getSurveysArray(line).find(s => String(s.survey_id) === String(surveyId));
                const onlySurvey = getSurveysArray(line).filter(s => !getFullyExcludedSurveyIds(line).includes(String(s.survey_id))).length <= 1;
                setShowExclusionModal(false);
                setConfirmAction({
                    title: 'Exclure l\'enquête',
                    message: `Une retenue de 100 % revient à exclure entièrement l'enquête « ${survey?.survey_title || surveyId} » de la ligne de ${line.person_name}.`
                        + (onlySurvey ? ' C\'est la seule enquête payée de cette ligne : toute la ligne sera exclue.' : '')
                        + ' Voulez-vous l\'exclure ?',
                    action: async () => {
                        try {
                            setProcessing(true);
                            const response = await postSurveyExclusion(line, surveyId, reason);
                            if (response.data.success) {
                                setSuccessMessage(response.data.message || 'Enquête exclue');
                                setShowSuccessModal(true);
                                setExclusionTarget(null);
                                await fetchBatchDetails();
                            }
                        } catch (err) {
                            setError(err.response?.data?.message || 'Erreur lors de l\'exclusion');
                        } finally {
                            setProcessing(false);
                        }
                    },
                });
                setShowConfirmModal(true);
                return;
            }
            try {
                setExclusionProcessing(true);
                const response = await postSurveyExclusion(line, surveyId, reason, pct);
                if (response.data.success) {
                    setSuccessMessage(response.data.message || 'Retenue appliquée');
                    setShowSuccessModal(true);
                    setShowExclusionModal(false);
                    setExclusionTarget(null);
                    setExclusionReason('');
                    await fetchBatchDetails();
                }
            } catch (err) {
                setExclusionModalError(err.response?.data?.message || 'Erreur lors de la retenue');
            } finally {
                setExclusionProcessing(false);
            }
            return;
        }

        try {
            setExclusionProcessing(true);
            const { line, surveyId, mode } = exclusionTarget;
            const url = mode === 'freeze'
                ? `/payments/${id}/lines/${line.id}/freeze`
                : surveyId
                ? `/payments/${id}/lines/${line.id}/exclude-survey`
                : `/payments/${id}/lines/${line.id}/exclude`;
            const payload = surveyId
                ? { survey_id: surveyId, reason: exclusionReason }
                : { reason: exclusionReason };
            const response = await api.post(url, payload);
            if (response.data.success) {
                setSuccessMessage(response.data.message || 'Ligne exclue avec succès');
                setShowSuccessModal(true);
                setShowExclusionModal(false);
                setExclusionTarget(null);
                setExclusionReason('');
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error excluding line:', err);
            // Shown inside the modal: the page-level error sits behind it and went unnoticed
            setExclusionModalError(err.response?.data?.message || (exclusionTarget?.mode === 'freeze' ? 'Erreur lors du gel' : 'Erreur lors de l\'exclusion'));
        } finally {
            setExclusionProcessing(false);
        }
    };

    const handleReincludeLine = async (line, surveyId = null) => {
        const retenue = surveyId ? getRetenueForSurvey(line, surveyId) : null;
        setConfirmAction({
            title: retenue ? 'Annuler la retenue' : line.freeze_requested ? 'Annuler le gel' : 'Réinclure la ligne',
            message: retenue
                ? `Voulez-vous annuler la retenue de ${formatPct(retenue.percentage)} % sur « ${retenue.survey_title || surveyId} » pour ${line.person_name} ?`
                : surveyId
                ? `Voulez-vous réinclure cette enquête dans le paiement de ${line.person_name} ?`
                : line.freeze_requested
                ? `Voulez-vous annuler le gel et réinclure ${line.person_name} dans le paiement ?`
                : `Voulez-vous réinclure ${line.person_name} dans le paiement ?`,
            action: async () => {
                try {
                    setProcessing(true);
                    const payload = surveyId ? { survey_id: surveyId } : {};
                    const response = await api.post(`/payments/${id}/lines/${line.id}/reinclude`, payload);
                    if (response.data.success) {
                        setSuccessMessage(response.data.message || 'Ligne réincluse avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error reincluding line:', err);
                    setError(err.response?.data?.message || 'Erreur lors de la réinclusion');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    // ── Retenue de garantie (F2F): a survey_exclusion with kind 'retenue' withholds only X % of a survey
    const getRetenues = (line) => (Array.isArray(line?.adjustments) ? line.adjustments : [])
        .filter(adj => adj.type === 'survey_exclusion' && adj.kind === 'retenue');
    const getRetenueForSurvey = (line, surveyId) => getRetenues(line).find(adj => String(adj.survey_id) === String(surveyId));
    const formatPct = (p) => Number(p || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
    // Synthesis label for a survey's excluded amount: "exclue", "retenue" or both
    const excludedAmountLabel = (details) => {
        const types = new Set((details || []).map(d => (d.type === 'retenue' ? 'retenue' : 'exclue')));
        return types.size === 2 ? 'exclue / retenue' : types.has('retenue') ? 'retenue' : 'exclue';
    };
    const canRetenue = (line) => canExclude() && getUserSection() === 'f2f'
        && line.section === 'f2f' && line.person_type === 'supervisor'
        && line.payment_status !== 'excluded' && !line.freeze_requested && parseFloat(line.net || 0) > 0;

    const isLineExcluded = (line) => line.payment_status === 'excluded';
    const isLineFrozen = (line) => line.payment_status === 'excluded' && !!line.freeze_requested;
    // Any line with a non-zero net can be frozen (money or debt), except travel-expense lines and
    // lines already excluded (réintégrer first, then geler)
    const canFreezeLine = (line) => {
        if (line.freeze_requested || isLineExcluded(line) || isLinePartiallyExcluded(line)) return false;
        if (Math.abs(parseFloat(line.net || 0)) < 0.01) return false;
        if (line.detail?.is_advance_only) return false;
        return !(line.adjustments || []).some(adj => adj.type === 'advance_frais_deplacement');
    };
    const isLinePartiallyExcluded = (line) => {
        if (!line.adjustments || !Array.isArray(line.adjustments)) return false;
        return line.adjustments.some(adj => adj.type === 'survey_exclusion');
    };
    const getExcludedSurveyIds = (line) => {
        if (!line.excluded_surveys) return [];
        const ids = Array.isArray(line.excluded_surveys) ? line.excluded_surveys : [];
        return ids.map(id => String(id)); // Normalize to strings for consistent comparison
    };
    // Surveys excluded at 100 % (a retenue keeps its survey payable)
    const getFullyExcludedSurveyIds = (line) => {
        const retained = getRetenues(line).map(adj => String(adj.survey_id));
        return getExcludedSurveyIds(line).filter(id => !retained.includes(id));
    };

    /** Resolve excluded survey IDs to titles for display (partial exclusion: show names, not just count). */
    const getExcludedSurveyNames = (line) => {
        const ids = getFullyExcludedSurveyIds(line);
        if (ids.length === 0) return [];
        const lineSurveys = getSurveysArray(line);
        return ids.map(surveyId => {
            const inLine = lineSurveys.find(s => String(s.survey_id) === String(surveyId));
            if (inLine?.survey_title) return inLine.survey_title;
            const inBatch = batch?.surveys?.find(s => String(s.id) === String(surveyId));
            return inBatch?.title || inBatch?.name || `Enquête #${surveyId}`;
        });
    };

    const canExclude = () => {
        if (!batch || batch.status !== 'draft' || batch.creation_mode !== 'auto') return false;
        const section = getUserSection();
        if (!section) return false;
        if (batch.section_validation?.[section]?.validated) return false;
        return true;
    };

    // Bulk retenue (F2F, frontend only): applies the per-line retenue to every F2F line containing the survey(s)
    const [showBulkRetenueModal, setShowBulkRetenueModal] = useState(false);
    const [bulkRetenuePct, setBulkRetenuePct] = useState({}); // survey_id => % (empty = not selected)
    const [bulkRetenueReason, setBulkRetenueReason] = useState('');
    const [bulkRetenueProcessing, setBulkRetenueProcessing] = useState(false);
    const [bulkRetenueResult, setBulkRetenueResult] = useState(null); // { applied, skipped: [] }

    // Surveys with work this week in this lot's F2F lines: how many prestataires and the week's base (for the
    // retenue modal's search list and its estimate).
    const getF2fBatchSurveys = () => {
        const map = new Map();
        (batch?.lines || []).filter(l => l.section === 'f2f' && l.person_type === 'supervisor' && canRetenue(l)).forEach(l => {
            getSurveysArray(l).forEach(s => {
                if (!surveyHasWork(s)) return;
                const sid = String(s.survey_id);
                const cur = map.get(sid) || { sid, title: s.survey_title || `Enquête #${sid}`, lines: 0, base: 0 };
                cur.lines += 1;
                cur.base += retenueBase(s);
                map.set(sid, cur);
            });
        });
        return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
    };
    // "Garantie cumulée" chip on a pay line → details of each held retenue
    const [heldDetailLine, setHeldDetailLine] = useState(null);
    const [bulkRetenueSearch, setBulkRetenueSearch] = useState('');
    const [bulkRetenueSamePct, setBulkRetenueSamePct] = useState('');

    const openBulkRetenueModal = () => {
        setBulkRetenuePct({});
        setBulkRetenueReason('');
        setBulkRetenueResult(null);
        setBulkRetenueSearch('');
        setBulkRetenueSamePct('');
        setShowBulkRetenueModal(true);
    };

    const handleBulkRetenue = async () => {
        const entries = Object.entries(bulkRetenuePct)
            .map(([sid, v]) => ({ sid, pct: parseFloat(String(v).replace(',', '.')) }))
            .filter(e => e.pct >= 1 && e.pct < 100);
        if (!entries.length || !bulkRetenueReason.trim()) return;
        setBulkRetenueProcessing(true);
        let applied = 0;
        const skipped = [];
        const lines = (batch?.lines || []).filter(l => l.section === 'f2f' && l.person_type === 'supervisor');
        for (const line of lines) {
            for (const { sid, pct } of entries) {
                const survey = getSurveysArray(line).find(s => String(s.survey_id) === sid);
                if (!survey || !surveyHasWork(survey)) continue; // no work on this survey this week on this line
                const title = survey.survey_title || `Enquête #${sid}`;
                if (!canRetenue(line)) { skipped.push(`${line.person_name} — ${title} : ligne exclue, gelée ou montant nul`); continue; }
                if (getExcludedSurveyIds(line).includes(sid)) { skipped.push(`${line.person_name} — ${title} : déjà exclue ou retenue`); continue; }
                try {
                    await postSurveyExclusion(line, sid, bulkRetenueReason, pct);
                    applied++;
                } catch (err) {
                    skipped.push(`${line.person_name} — ${title} : ${err.response?.data?.message || 'erreur'}`);
                }
            }
        }
        setBulkRetenueProcessing(false);
        setBulkRetenueResult({ applied, skipped });
        await fetchBatchDetails();
    };

    // Bulk survey exclusion handlers
    const [showBulkExclusionModal, setShowBulkExclusionModal] = useState(false);
    const [bulkExclusionSurvey, setBulkExclusionSurvey] = useState(null);
    const [bulkExclusionReason, setBulkExclusionReason] = useState('');
    const [bulkExclusionProcessing, setBulkExclusionProcessing] = useState(false);

    const openBulkExcludeSurveyModal = (survey) => {
        setBulkExclusionSurvey(survey);
        setBulkExclusionReason('');
        setShowBulkExclusionModal(true);
    };

    const handleBulkExcludeSurvey = async () => {
        if (!bulkExclusionSurvey || !bulkExclusionReason.trim()) return;
        try {
            setBulkExclusionProcessing(true);
            const response = await api.post(`/payments/${id}/exclude-survey-batch`, {
                survey_id: bulkExclusionSurvey.id,
                reason: bulkExclusionReason,
            });
            if (response.data.success) {
                setSuccessMessage(response.data.message || 'Enquête exclue avec succès');
                setShowSuccessModal(true);
                setShowBulkExclusionModal(false);
                setBulkExclusionSurvey(null);
                setBulkExclusionReason('');
                await fetchBatchDetails();
            }
        } catch (err) {
            console.error('Error bulk excluding survey:', err);
            setError(err.response?.data?.message || 'Erreur lors de l\'exclusion de l\'enquête');
        } finally {
            setBulkExclusionProcessing(false);
        }
    };

    const handleBulkReincludeSurvey = async (survey) => {
        setConfirmAction({
            title: 'Réinclure l\'enquête',
            message: `Voulez-vous réinclure l'enquête "${survey.title}" dans toutes les lignes de paiement de votre section ?`,
            action: async () => {
                try {
                    setProcessing(true);
                    const response = await api.post(`/payments/${id}/reinclude-survey-batch`, {
                        survey_id: survey.id,
                    });
                    if (response.data.success) {
                        setSuccessMessage(response.data.message || 'Enquête réintégrée avec succès');
                        setShowSuccessModal(true);
                        await fetchBatchDetails();
                    }
                } catch (err) {
                    console.error('Error bulk reincluding survey:', err);
                    setError(err.response?.data?.message || 'Erreur lors de la réintégration de l\'enquête');
                } finally {
                    setProcessing(false);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const isSurveyExcludedFromBatch = (surveyId) => {
        if (!batch?.lines) return false;
        const section = getUserSection();
        const sid = String(surveyId);
        // Lines that contain this survey: filter by section only when user is a section head
        const linesWithSurvey = batch.lines.filter(line => {
            if (section != null && line.section !== section) return false;
            const surveys = getSurveysArray(line);
            const hasSurvey = surveys.some(s => String(s.survey_id) === sid);
            const inCompleted = Array.isArray(line.completed_surveys) && line.completed_surveys.map(String).includes(sid);
            return hasSurvey || inCompleted;
        });
        if (linesWithSurvey.length === 0) return false;
        // Survey is excluded from batch if every line that has it has excluded it (full line or this survey at 100 %)
        return linesWithSurvey.every(line => {
            if (line.payment_status === 'excluded') return true;
            return getFullyExcludedSurveyIds(line).includes(sid);
        });
    };

    const isSurveyForUserSection = (survey) => {
        const section = getUserSection();
        if (!section) return false;
        if (section === 'cati') return survey.poste_qcode !== 'ref';
        if (section === 'f2f') return survey.poste_qcode === 'ref';
        return false; // Controllers don't have surveys
    };

    const handleDownloadExport = async (exportId, exportType) => {
        try {
            setProcessing(true);
            const response = await api.get(`/payments/exports/${exportId}/download`, {
                responseType: 'blob' // Important for file download
            });

            // Create a blob from the response
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or use default
            let filename = `paiement_${exportType === 'ALL' ? 'complet' : exportType}_${new Date().getTime()}.xlsx`;
            const contentDisposition = response.headers['content-disposition'];
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                    // Ensure it has .xlsx extension
                    if (!filename.endsWith('.xlsx')) {
                        filename = filename.replace(/\.(csv|xls)$/, '.xlsx');
                    }
                }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading export:', err);
            setError('Erreur lors du téléchargement du fichier');
        } finally {
            setProcessing(false);
        }
    };

    const handleAdjustmentClick = (adjustment, line) => {
        // Calculate carryover quantity and collect survey-level data if this is a carryover adjustment
        let carryoverQty = null;
        let carryoverSurveys = [];
        // A carried row built with its own split (amount + exact completes per survey): show THAT row's
        // completes, not the whole line's carried quantity.
        const ownSplit = adjustment.type === 'carryover' && Array.isArray(adjustment.surveys)
            ? adjustment.surveys.filter(s => s.qty !== null && s.qty !== undefined && parseFloat(s.qty) > 0)
            : [];
        if (ownSplit.length > 0) {
            const lineSurveys = getSurveysArray(line);
            ownSplit.forEach(s => {
                const ls = lineSurveys.find(x => String(x.survey_id) === String(s.survey_id));
                carryoverSurveys.push({
                    survey_id: s.survey_id,
                    survey_title: ls?.survey_title || batch?.surveys?.find(x => String(x.id) === String(s.survey_id))?.title || `Enquête #${s.survey_id}`,
                    qty: Math.round(parseFloat(s.qty) * 100) / 100,
                    payment_type: ls?.payment_type || 'per_complete',
                });
                carryoverQty = Math.round(((carryoverQty || 0) + parseFloat(s.qty)) * 100) / 100;
            });
        } else if (adjustment.type === 'carryover') {
            const surveys = getSurveysArray(line);
            // Collect survey-level carryover data
            surveys.forEach(survey => {
                let surveyCarryoverQty = 0;
                if (survey.is_carryover_only) {
                    // For carryover-only surveys, the qty itself is the carryover quantity
                    surveyCarryoverQty = parseFloat(survey.qty) || 0;
                } else {
                    // For surveys with current work, use carryover_qty if available
                    surveyCarryoverQty = parseFloat(survey.carryover_qty) || 0;
                }
                
                if (surveyCarryoverQty > 0) {
                    carryoverSurveys.push({
                        survey_id: survey.survey_id,
                        survey_title: survey.survey_title,
                        qty: surveyCarryoverQty,
                        payment_type: survey.payment_type || 'per_complete',
                    });
                    carryoverQty = (carryoverQty || 0) + surveyCarryoverQty;
                }
            });
        }
        
        // Add additional context to the adjustment
        const adjustmentWithContext = {
            ...adjustment,
            batch_ref: `LOT-${batch.id}`,
            created_at: adjustment.at || line.created_at || batch.created_at,  // Use adjustment timestamp if available
            carryover_qty: carryoverQty,  // Add carryover quantity for carryover adjustments
            carryover_surveys: carryoverSurveys  // Add survey-level breakdown
        };
        setSelectedAdjustment(adjustmentWithContext);
        setShowAdjustmentDetailModal(true);
    };

    const fetchCompletionData = async () => {
        try {
            setLoadingCompletion(true);
            const response = await api.get(`/payments/${id}/completion-details`);
            if (response.data.success) {
                setCompletionData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching completion data:', err);
            setError('Erreur lors du chargement des données de completion');
        } finally {
            setLoadingCompletion(false);
        }
    };

    const handleExportCompletion = async () => {
        try {
            setExportingCompletion(true);
            const response = await api.get(`/payments/${id}/export-completion`, {
                responseType: 'blob'
            });

            // Create a blob from the response
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or use default
            let filename = `completion_lot_${id}_${new Date().getTime()}.xlsx`;
            const contentDisposition = response.headers['content-disposition'];
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting completion data:', err);
            setError('Erreur lors de l\'exportation des données de completion');
        } finally {
            setExportingCompletion(false);
        }
    };

    const handleDownloadProofFile = async (fileId, filename) => {
        try {
            setProcessing(true);
            const response = await api.get(`/payments/proof-files/${fileId}/download`, {
                responseType: 'blob'
            });

            // Create a blob from the response
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/octet-stream'
            });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or use provided filename
            let downloadFilename = filename || `proof_file_${fileId}`;
            const contentDisposition = response.headers['content-disposition'];
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSuccessMessage('Fichier téléchargé avec succès');
            setShowSuccessModal(true);
        } catch (err) {
            console.error('Error downloading proof file:', err);
            setError('Erreur lors du téléchargement du fichier de preuve');
        } finally {
            setProcessing(false);
        }
    };

    const getCompletionStatusBadge = (status) => {
        const statusConfig = {
            paid: { icon: faCheckCircle, text: 'Payé', color: 'bg-green-100 text-green-700' },
            unpaid: { icon: faTimesCircle, text: 'Non payé', color: 'bg-red-100 text-red-700' },
            rejected: { icon: faExclamationTriangle, text: 'Annulé', color: 'bg-red-100 text-red-700' },
            insufficient: { icon: faQuestionCircle, text: 'Insuffisant', color: 'bg-orange-100 text-orange-700' },
            paid_via_carryover: { icon: faCheck, text: 'Payé via report', color: 'bg-blue-100 text-blue-700' },
            excluded: { icon: faTimesCircle, text: 'Exclu', color: 'bg-amber-100 text-amber-800' },
        };

        const config = statusConfig[status] || { icon: faClock, text: status || 'En attente', color: 'bg-gray-100 text-gray-700' };

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <FontAwesomeIcon icon={config.icon} className="mr-1.5" />
                {config.text}
            </span>
        );
    };

    const getStatusStats = (lines) => {
        if (!lines) return { paid: 0, unpaid: 0, rejected: 0, insufficient: 0, excluded: 0, total: 0 };
        const stats = lines.reduce((acc, line) => {
            acc[line.payment_status] = (acc[line.payment_status] || 0) + 1;
            acc.total += 1;
            return acc;
        }, { total: 0 });
        return stats;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { icon: faClock, text: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
            validated: { icon: faCheck, text: 'Validé', color: 'bg-blue-100 text-blue-700' },
            locked: { icon: faLock, text: 'Verrouillé', color: 'bg-orange-100 text-orange-700' },
            exported: { icon: faFileExport, text: 'Exporté', color: 'bg-green-100 text-green-700' },
        };

        const config = statusConfig[status] || { icon: faClock, text: status, color: 'bg-gray-100 text-gray-700' };

        return (
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${config.color}`}>
                <FontAwesomeIcon icon={config.icon} className="mr-2" />
                {config.text}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-red-600 mb-4" />
                    <p className="text-gray-600">Chargement du lot de paiement...</p>
                </div>
            </div>
        );
    }

    if (error && !batch) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
                        {error}
                    </div>
                    <button
                        onClick={() => navigate('/payments')}
                        className="mt-4 text-gray-600 hover:text-gray-900 flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Retour aux lots de paiement
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 break-words">
                                {batch.creation_mode === 'express' ? 'Frais de Déplacement' : 'Lot de Paiement'} #{batch.id}
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 break-words">
                                {/* Type intentionally omitted: the period alone reads better,
                                    and the badges beside the title already carry the kind. */}
                                {new Date(batch.period_start).toLocaleDateString('fr-FR')} - {new Date(batch.period_end).toLocaleDateString('fr-FR')}
                            </p>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2">
                            {batch.creation_mode === 'auto' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                    Auto
                                </span>
                            )}
                            {batch.creation_mode === 'express' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                    Express
                                </span>
                            )}
                            {getStatusBadge(batch.status)}
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}


                {/* Summary Cards */}
                {(() => {
                    const userSection = getUserSection();
                    const isAutoSectionHead = batch.creation_mode === 'auto' && userSection && batch.status === 'draft';
                    const displayLines = isAutoSectionHead
                        ? (batch.lines || []).filter(l => l.section === userSection)
                        : batch.lines || [];
                    const sectionLabel = isAutoSectionHead
                        ? { cati: 'CATI', f2f: 'F2F', controller: 'Contrôleurs' }[userSection]
                        : null;
                    const displayGross = isAutoSectionHead
                        ? displayLines.reduce((sum, l) => sum + parseFloat(l.total || 0), 0)
                        : parseFloat(batch.totals_gross);
                    const displayNet = isAutoSectionHead
                        ? displayLines.filter(l => !l.on_hold && l.payment_status !== 'excluded' && !(l.payment_status === 'unpaid' && parseFloat(l.net || 0) < 200)).reduce((sum, l) => sum + parseFloat(l.net || 0), 0)
                        : parseFloat(batch.totals_net);

                    return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
                                <div className="text-xs sm:text-sm text-gray-600 mb-1">
                                    {isAutoSectionHead ? `Personnes ${sectionLabel}` : 'Nombre de Personnes'}
                                </div>
                                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{displayLines.length}</div>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
                                <div className="text-xs sm:text-sm text-gray-600 mb-1">
                                    {isAutoSectionHead ? `Total Brut ${sectionLabel}` : 'Total Brut'}
                                </div>
                                <div className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                                    {displayGross.toFixed(2)} MAD
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
                                <div className="text-xs sm:text-sm text-gray-600 mb-1">
                                    {isAutoSectionHead ? `Total Net ${sectionLabel}` : 'Total Net'}
                                </div>
                                {batch.creation_mode === 'express' && batch.caisse?.has_credit ? (
                                    <>
                                        {/* Prominent = what to actually hand to resp admin (total net minus caisse) */}
                                        <div className="text-xl sm:text-2xl font-bold text-purple-700 break-words">
                                            {parseFloat(batch.caisse.net_a_decaisser).toFixed(2)} MAD
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {displayNet.toFixed(2)} (total) − {parseFloat(batch.caisse.credit_applied).toFixed(2)} (déjà en caisse)
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-400">
                                            <span>Couvert par la caisse (annulation(s)) :</span>
                                            {[...new Set((batch.caisse.sources || []).map(s => s.source_batch_id).filter(Boolean))].map(lotId => (
                                                <button
                                                    key={lotId}
                                                    onClick={() => navigate(`/payments/${lotId}`)}
                                                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 hover:bg-purple-100 font-medium"
                                                >
                                                    Lot #{lotId}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-xl sm:text-2xl font-bold text-green-600 break-words">
                                            {displayNet.toFixed(2)} MAD
                                        </div>
                                        {(user?.role === 'chef_terrain_superieur' || isAdminViewer) && batch.creation_mode === 'auto' && batch.status === 'draft' && !batch.all_sections_validated && (
                                            <div className="text-xs text-amber-600 mt-1">Provisoire — en attente de validation des sections</div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Synthesis Panel - Admin roles, section heads, and delegated users */}
                {(['administrateur_general', 'administrateur_general_superieur', 'resp_admin', 'chef_terrain_superieur', 'resp_cati', 'chef_terrain', 'resp_controle'].includes(user?.role) || batch.is_delegated_to_me) && ['locked', 'validated', 'exported', 'confirmed'].includes(batch.status) && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                        {/* Header */}
                        <div
                            className="flex items-center justify-between p-4 sm:p-6 cursor-pointer select-none"
                            onClick={() => setShowSynthesis(!showSynthesis)}
                        >
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                Synthese du Lot
                            </h3>
                            <FontAwesomeIcon
                                icon={showSynthesis ? faChevronUp : faChevronDown}
                                className="text-gray-500"
                            />
                        </div>

                        {showSynthesis && (
                            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                                {loadingSynthesis ? (
                                    <div className="flex items-center justify-center py-8">
                                        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400 mr-3" />
                                        <span className="text-gray-500">Chargement de la synthese...</span>
                                    </div>
                                ) : synthesisData ? (
                                    <div className="space-y-1">
                                        {/* Section Rows */}
                                        {Object.entries(synthesisData.sections).map(([sectionKey, section]) => {
                                            const isExpanded = expandedSynthesisSections[sectionKey];
                                            const sectionBadgeColor = 'bg-gray-800 text-white';

                                            return (
                                                <div key={sectionKey} className="border border-gray-200 rounded-lg overflow-hidden">
                                                    {/* Section Summary Row */}
                                                    <div
                                                        className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                                        onClick={() => toggleSynthesisSection(sectionKey)}
                                                    >
                                                        {/* Mobile: stacked layout */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                                <FontAwesomeIcon
                                                                    icon={faChevronDown}
                                                                    className={`text-gray-400 text-xs w-3 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                                                                />
                                                                <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${sectionBadgeColor} flex-shrink-0`}>
                                                                    {section.label}
                                                                </span>
                                                                <span className="text-xs sm:text-sm text-gray-600 truncate">
                                                                    {section.lines_count} ligne{section.lines_count > 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                                                                <div className="text-right">
                                                                    <div className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">
                                                                        {Number(section.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                                                                    </div>
                                                                </div>
                                                                {batch?.creation_mode !== 'express' && (
                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                    {section.validated ? (
                                                                        <>
                                                                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-sm" />
                                                                            <span className="text-xs text-green-700 truncate max-w-[60px] sm:max-w-[80px] hidden sm:inline">{section.validated_by_name}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon icon={faClock} className="text-yellow-500 text-sm" />
                                                                            <span className="text-xs text-yellow-700 hidden sm:inline">En attente</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Expanded: Controller section — controls per survey + tasks per type */}
                                                    {isExpanded && section.is_controller && (
                                                        <div className="border-t border-gray-200 bg-gray-50 p-3 sm:p-4 space-y-4">
                                                            {/* Controls per survey */}
                                                            {section.surveys && section.surveys.length > 0 && (
                                                                <div>
                                                                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Controles par enquete</div>
                                                                    {/* Mobile */}
                                                                    <div className="block sm:hidden space-y-2">
                                                                        {section.surveys.map((survey) => (
                                                                            <div key={survey.survey_id} className="bg-white rounded-lg border border-gray-200 p-3">
                                                                                <div className="font-medium text-gray-900 text-sm mb-1">
                                                                                    {survey.survey_title}
                                                                                    {survey.client_name && <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{survey.client_name}</span>}
                                                                                </div>
                                                                                <div className="text-xs text-gray-500 mb-2">{survey.current_batch.controllers_count} controleur{survey.current_batch.controllers_count > 1 ? 's' : ''} · {survey.current_batch.days} j</div>
                                                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                                                    <div>
                                                                                        <div className="text-gray-500 font-medium mb-0.5">Lot Actuel</div>
                                                                                        <div className="font-semibold">{survey.current_batch.controls} ctrl</div>
                                                                                        <div>{Number(survey.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                        {survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount && (
                                                                                            <div
                                                                                                className={`underline decoration-dotted cursor-pointer ${survey.current_batch.adjusted_amount > survey.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}
                                                                                                onClick={(e) => { e.stopPropagation(); setAdjustmentBreakdown({ ...survey, _isController: true }); }}
                                                                                            >
                                                                                                Ajusté: {Number(survey.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="text-gray-500 font-medium mb-0.5">Cumulé</div>
                                                                                        <div className="text-gray-600">{survey.cumulative.controls} ctrl</div>
                                                                                        <div className="text-gray-600">{Number(survey.cumulative.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="text-gray-500 font-medium mb-0.5">Total</div>
                                                                                        <div className="font-bold">{survey.grand_total.controls} ctrl</div>
                                                                                        <div className="font-bold">{Number(survey.grand_total.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    {/* Desktop */}
                                                                    <div className="hidden sm:block overflow-x-auto">
                                                                        <table className="min-w-full text-sm">
                                                                            <thead>
                                                                                <tr className="text-xs text-gray-400 border-b border-gray-300">
                                                                                    <th className="text-left py-1.5 px-2">Enquete</th>
                                                                                    <th className="text-right py-1.5 px-2">Controleurs</th>
                                                                                    <th className="text-right py-1.5 px-2">Jours</th>
                                                                                    <th className="text-right py-1.5 px-2">Controles</th>
                                                                                    <th className="text-right py-1.5 px-2 border-l border-gray-300">Lot Actuel</th>
                                                                                    <th className="text-right py-1.5 px-2">Ajusté</th>
                                                                                    <th className="text-right py-1.5 px-2 border-l border-gray-300">Cumulé</th>
                                                                                    <th className="text-right py-1.5 px-2 border-l border-gray-300">Total</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200">
                                                                                {section.surveys.map((survey) => (
                                                                                    <tr key={survey.survey_id} className="hover:bg-white transition-colors">
                                                                                        <td className="py-2 px-2 font-medium text-gray-900">
                                                                                            {survey.survey_title}
                                                                                            {survey.client_name && <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{survey.client_name}</span>}
                                                                                        </td>
                                                                                        <td className="py-2 px-2 text-right text-gray-600">{survey.current_batch.controllers_count}</td>
                                                                                        <td className="py-2 px-2 text-right text-gray-600">{survey.current_batch.days}</td>
                                                                                        <td className="py-2 px-2 text-right font-medium">{survey.current_batch.controls}</td>
                                                                                        <td className="py-2 px-2 text-right font-medium border-l border-gray-200">{Number(survey.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                                                                        <td
                                                                                            className={`py-2 px-2 text-right font-medium ${survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                                                                            onClick={() => survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount && setAdjustmentBreakdown({ ...survey, _isController: true })}
                                                                                            title={survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount ? 'Cliquer pour voir le détail' : undefined}
                                                                                        >
                                                                                            {survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount ? (
                                                                                                <span className={`underline decoration-dotted decoration-current ${survey.current_batch.adjusted_amount > survey.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}>
                                                                                                    {Number(survey.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="py-2 px-2 text-right text-gray-600 border-l border-gray-200">{Number(survey.cumulative.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                                                                        <td className="py-2 px-2 text-right font-semibold border-l border-gray-200">{Number(survey.grand_total.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Tasks per type */}
                                                            {section.tasks && section.tasks.length > 0 && (
                                                                <div>
                                                                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Autres Taches</div>
                                                                    {/* Mobile */}
                                                                    <div className="block sm:hidden space-y-2">
                                                                        {section.tasks.map((task) => (
                                                                            <div key={task.task_type} className="bg-white rounded-lg border border-gray-200 p-3">
                                                                                <div className="font-medium text-gray-900 text-sm mb-1">{task.task_label}</div>
                                                                                <div className="text-xs text-gray-500 mb-2">{task.current_batch.controllers_count} controleur{task.current_batch.controllers_count > 1 ? 's' : ''} · {task.current_batch.days} j</div>
                                                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                                                    <div>
                                                                                        <div className="text-gray-500 font-medium mb-0.5">Montant</div>
                                                                                        <div className="font-semibold text-gray-900">{Number(task.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                    </div>
                                                                                    {task.current_batch.adjusted_amount !== undefined && task.current_batch.adjusted_amount !== task.current_batch.amount && (
                                                                                        <div
                                                                                            className="cursor-pointer"
                                                                                            onClick={(e) => { e.stopPropagation(); setAdjustmentBreakdown({ survey_title: task.task_label, current_batch: task.current_batch, _isController: true, _isTask: true }); }}
                                                                                        >
                                                                                            <div className="text-gray-500 font-medium mb-0.5">Ajusté</div>
                                                                                            <div className={`font-semibold underline decoration-dotted ${task.current_batch.adjusted_amount > task.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}>
                                                                                                {Number(task.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    {/* Desktop */}
                                                                    <div className="hidden sm:block overflow-x-auto">
                                                                        <table className="min-w-full text-sm">
                                                                            <thead>
                                                                                <tr className="text-xs text-gray-400 border-b border-gray-300">
                                                                                    <th className="text-left py-1.5 px-2">Autre Tache</th>
                                                                                    <th className="text-right py-1.5 px-2">Controleurs</th>
                                                                                    <th className="text-right py-1.5 px-2">Jours</th>
                                                                                    <th className="text-right py-1.5 px-2 border-l border-gray-300">Montant</th>
                                                                                    <th className="text-right py-1.5 px-2">Ajusté</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200">
                                                                                {section.tasks.map((task) => (
                                                                                    <tr key={task.task_type} className="hover:bg-white transition-colors">
                                                                                        <td className="py-2 px-2 font-medium text-gray-900">{task.task_label}</td>
                                                                                        <td className="py-2 px-2 text-right text-gray-600">{task.current_batch.controllers_count}</td>
                                                                                        <td className="py-2 px-2 text-right text-gray-600">{task.current_batch.days}</td>
                                                                                        <td className="py-2 px-2 text-right font-medium text-gray-900 border-l border-gray-200">{Number(task.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                                                                        <td
                                                                                            className={`py-2 px-2 text-right font-medium ${task.current_batch.adjusted_amount !== undefined && task.current_batch.adjusted_amount !== task.current_batch.amount ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                                                                            onClick={() => task.current_batch.adjusted_amount !== undefined && task.current_batch.adjusted_amount !== task.current_batch.amount && setAdjustmentBreakdown({ survey_title: task.task_label, current_batch: task.current_batch, _isController: true, _isTask: true })}
                                                                                            title={task.current_batch.adjusted_amount !== undefined && task.current_batch.adjusted_amount !== task.current_batch.amount ? 'Cliquer pour voir le détail' : undefined}
                                                                                        >
                                                                                            {task.current_batch.adjusted_amount !== undefined && task.current_batch.adjusted_amount !== task.current_batch.amount ? (
                                                                                                <span className={`underline decoration-dotted decoration-current ${task.current_batch.adjusted_amount > task.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}>
                                                                                                    {Number(task.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Reports — standalone section without cumulative */}
                                                            {section.adjustments && section.adjustments.total !== 0 && (
                                                                <div>
                                                                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Reports</div>
                                                                    {/* Mobile */}
                                                                    <div className="block sm:hidden">
                                                                        <div className="bg-white rounded-lg border border-gray-200 p-3">
                                                                            <div className="font-medium text-gray-900 text-sm mb-2">Total ajustements</div>
                                                                            <div className="space-y-1.5 text-xs">
                                                                                {section.adjustments.carryover !== 0 && (
                                                                                    <div className="flex justify-between">
                                                                                        <span className="text-gray-600">Reports</span>
                                                                                        <span
                                                                                            className={`font-semibold ${section.adjustments.carryover > 0 ? 'text-green-700' : 'text-red-700'} ${section.adjustments.carryover_details?.length > 0 ? 'underline decoration-dotted cursor-pointer' : ''}`}
                                                                                            onClick={() => section.adjustments.carryover_details?.length > 0 && setMontantBreakdown({ survey_title: 'Reports', _isController: true, current_batch: { amount: 0, carryover_amount: section.adjustments.carryover, carryover_details: section.adjustments.carryover_details } })}
                                                                                        >
                                                                                            {section.adjustments.carryover > 0 ? '+' : ''}{Number(section.adjustments.carryover).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {section.adjustments.bonus !== 0 && (
                                                                                    <div className="flex justify-between">
                                                                                        <span className="text-gray-600">Bonus</span>
                                                                                        <span className="font-semibold text-green-700">+{Number(section.adjustments.bonus).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</span>
                                                                                    </div>
                                                                                )}
                                                                                {section.adjustments.penalty !== 0 && (
                                                                                    <div className="flex justify-between">
                                                                                        <span className="text-gray-600">Pénalités</span>
                                                                                        <span className="font-semibold text-red-700">{Number(section.adjustments.penalty).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</span>
                                                                                    </div>
                                                                                )}
                                                                                <div className="flex justify-between border-t border-gray-200 pt-1.5 mt-1.5">
                                                                                    <span className="font-medium text-gray-900">Net</span>
                                                                                    <span className={`font-bold ${section.adjustments.total > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                                                                        {section.adjustments.total > 0 ? '+' : ''}{Number(section.adjustments.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {/* Desktop */}
                                                                    <div className="hidden sm:block overflow-x-auto">
                                                                        <table className="min-w-full text-sm">
                                                                            <thead>
                                                                                <tr className="text-xs text-gray-400 border-b border-gray-300">
                                                                                    <th className="text-left py-1.5 px-2">Type</th>
                                                                                    <th className="text-right py-1.5 px-2 border-l border-gray-300">Montant</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200">
                                                                                {section.adjustments.carryover !== 0 && (
                                                                                    <tr className="hover:bg-white transition-colors">
                                                                                        <td className="py-2 px-2 font-medium text-gray-900">Reports</td>
                                                                                        <td className={`py-2 px-2 text-right font-medium border-l border-gray-200 ${section.adjustments.carryover > 0 ? 'text-green-700' : 'text-red-700'} ${section.adjustments.carryover_details?.length > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                                                                            onClick={() => section.adjustments.carryover_details?.length > 0 && setMontantBreakdown({ survey_title: 'Reports', _isController: true, current_batch: { amount: 0, carryover_amount: section.adjustments.carryover, carryover_details: section.adjustments.carryover_details } })}
                                                                                        >
                                                                                            <span className={section.adjustments.carryover_details?.length > 0 ? 'underline decoration-dotted decoration-current' : ''}>
                                                                                                {section.adjustments.carryover > 0 ? '+' : ''}{Number(section.adjustments.carryover).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                                {section.adjustments.bonus !== 0 && (
                                                                                    <tr className="hover:bg-white transition-colors">
                                                                                        <td className="py-2 px-2 font-medium text-gray-900">Bonus</td>
                                                                                        <td className="py-2 px-2 text-right font-medium text-green-700 border-l border-gray-200">
                                                                                            +{Number(section.adjustments.bonus).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                                {section.adjustments.penalty !== 0 && (
                                                                                    <tr className="hover:bg-white transition-colors">
                                                                                        <td className="py-2 px-2 font-medium text-gray-900">Pénalités</td>
                                                                                        <td className="py-2 px-2 text-right font-medium text-red-700 border-l border-gray-200">
                                                                                            {Number(section.adjustments.penalty).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                            <tfoot>
                                                                                <tr className="border-t border-gray-300 font-semibold">
                                                                                    <td className="py-2 px-2 text-gray-900">Net ajustements</td>
                                                                                    <td className={`py-2 px-2 text-right border-l border-gray-200 ${section.adjustments.total > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                                                                        {section.adjustments.total > 0 ? '+' : ''}{Number(section.adjustments.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                    </td>
                                                                                </tr>
                                                                            </tfoot>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Unattributed (no controls, no tasks) */}
                                                            {section.unattributed_amount > 0 && (
                                                                <div className="text-xs text-gray-500 italic">
                                                                    {Number(section.unattributed_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD non attribue ({section.unattributed_controllers_count} controleur{section.unattributed_controllers_count > 1 ? 's' : ''} sans controles ni taches)
                                                                </div>
                                                            )}

                                                            {/* Nothing at all */}
                                                            {(!section.surveys || section.surveys.length === 0) && (!section.tasks || section.tasks.length === 0) && !section.unattributed_amount && !(section.adjustments && section.adjustments.total !== 0) && (
                                                                <div className="text-sm text-gray-500 italic">
                                                                    Aucune donnee de controle pour cette periode
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Expanded: CATI/F2F Per-Survey — card layout on mobile, table on sm+ */}
                                                    {isExpanded && !section.is_controller && section.surveys && section.surveys.length > 0 && (
                                                        <div className="border-t border-gray-200 bg-gray-50">
                                                            {/* Mobile: Card layout */}
                                                            <div className="block sm:hidden p-3 space-y-3">
                                                                {section.surveys.map((survey) => {
                                                                    const budgetColors = getBudgetColor(survey.grand_total.budget_percentage);
                                                                    return (
                                                                        <div key={survey.survey_id} className="bg-white rounded-lg border border-gray-200 p-3">
                                                                            <div className="font-medium text-gray-900 text-sm mb-2">
                                                                                {survey.survey_title}
                                                                                {survey.client_name && <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{survey.client_name}</span>}
                                                                            </div>
                                                                            <div className="text-xs text-gray-500 mb-2">
                                                                                {survey.payment_type === 'per_day'
                                                                                    ? `PPJ: ${Number(survey.price_per_day).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD`
                                                                                    : `PPC: ${Number(survey.price_per_complete).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD`
                                                                                }
                                                                            </div>
                                                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                                                <div>
                                                                                    <div className="text-gray-500 font-medium mb-0.5">Lot Actuel</div>
                                                                                    <div className="font-semibold text-gray-900">
                                                                                        {survey.payment_type === 'per_day' ? fmtDayQty(survey.current_batch.completes) : Number(survey.current_batch.completes).toLocaleString('fr-FR')}
                                                                                    </div>
                                                                                    {survey.payment_type !== 'per_day' && Number(survey.current_batch.excluded_completes || 0) > 0 && (
                                                                                        <div className="text-yellow-700">Ajusté : {(Number(survey.current_batch.completes) - Number(survey.current_batch.excluded_completes)).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</div>
                                                                                    )}
                                                                                    <div
                                                                                        className={`text-gray-700 ${survey.current_batch.carryover_amount > 0 ? 'underline decoration-dotted cursor-pointer' : ''}`}
                                                                                        onClick={() => survey.current_batch.carryover_amount > 0 && setMontantBreakdown(survey)}
                                                                                    >
                                                                                        {Number(survey.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                                                                                                            </div>
                                                                                    {survey.current_batch.adjusted_amount !== undefined && survey.current_batch.adjusted_amount !== survey.current_batch.amount && (
                                                                                        <div
                                                                                            className={`font-semibold cursor-pointer underline decoration-dotted ${survey.current_batch.adjusted_amount > survey.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}
                                                                                            onClick={(e) => { e.stopPropagation(); setAdjustmentBreakdown(survey); }}
                                                                                        >
                                                                                            Ajusté: {Number(survey.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-gray-500 font-medium mb-0.5">Cumulé</div>
                                                                                    <div className="text-gray-600">
                                                                                        {survey.payment_type === 'per_day' ? fmtDayQty(survey.cumulative.completes) : Number(survey.cumulative.completes).toLocaleString('fr-FR')}
                                                                                    </div>
                                                                                    <div className="text-gray-600">{Number(survey.cumulative.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-gray-500 font-medium mb-0.5">Total</div>
                                                                                    <div className="font-bold text-gray-900">
                                                                                        {survey.payment_type === 'per_day' ? fmtDayQty(survey.grand_total.completes) : Number(survey.grand_total.completes).toLocaleString('fr-FR')}
                                                                                    </div>
                                                                                    <div className="font-bold text-gray-900">{Number(survey.grand_total.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                </div>
                                                                            </div>
                                                                            {survey.total_budget > 0 && (
                                                                                <div className="mt-2 pt-2 border-t border-gray-100">
                                                                                    <div className="flex items-center justify-between mb-1">
                                                                                        <span className="text-xs text-gray-500 font-medium">Budget</span>
                                                                                        <span className="text-xs text-gray-500">
                                                                                            {Number(survey.total_budget).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-2">
                                                                                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                                                            <div
                                                                                                className={`h-full rounded-full ${budgetColors.bar}`}
                                                                                                style={{ width: `${Math.min(survey.grand_total.budget_percentage, 100)}%` }}
                                                                                            />
                                                                                        </div>
                                                                                        <span className={`text-xs font-semibold ${budgetColors.text} whitespace-nowrap`}>
                                                                                            {survey.grand_total.budget_percentage}%
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {survey.current_batch.excluded_amount > 0 && (
                                                                                <div
                                                                                    className="text-xs text-amber-600 mt-1 underline decoration-dotted cursor-pointer"
                                                                                    onClick={(e) => { e.stopPropagation(); setExcludedBreakdown(survey); }}
                                                                                >
                                                                                    {Number(survey.current_batch.excluded_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD {excludedAmountLabel(survey.current_batch.excluded_details)}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                                {/* Mobile totals */}
                                                                {(() => {
                                                                    const splitQte = (getter) => {
                                                                        const qte = section.surveys.filter(sv => sv.payment_type !== 'per_day').reduce((s, sv) => s + getter(sv), 0);
                                                                        const days = section.surveys.filter(sv => sv.payment_type === 'per_day').reduce((s, sv) => s + getter(sv), 0);
                                                                        return (
                                                                            <>
                                                                                {qte > 0 && days > 0
                                                                                    ? <div>{qte.toLocaleString('fr-FR')} <span className="text-gray-400">+</span> {days.toLocaleString('fr-FR')} <span className="text-gray-400">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                    : qte > 0 ? <div>{qte.toLocaleString('fr-FR')}</div>
                                                                                    : days > 0 ? <div>{days.toLocaleString('fr-FR')} <span className="text-gray-400">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                    : <div>0</div>
                                                                                }
                                                                            </>
                                                                        );
                                                                    };
                                                                    return (
                                                                        <div className="bg-gray-100 rounded-lg border border-gray-300 p-3">
                                                                            <div className="font-bold text-gray-900 text-xs mb-1">Total</div>
                                                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                                                <div>
                                                                                    <div className="font-bold">{splitQte(sv => sv.current_batch.completes)}</div>
                                                                                    <div className="font-bold">{section.surveys.reduce((s, sv) => s + sv.current_batch.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div>{splitQte(sv => sv.cumulative.completes)}</div>
                                                                                    <div>{section.surveys.reduce((s, sv) => s + sv.cumulative.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="font-bold">{splitQte(sv => sv.grand_total.completes)}</div>
                                                                                    <div className="font-bold">{section.surveys.reduce((s, sv) => s + sv.grand_total.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>

                                                            {/* Desktop: Table layout */}
                                                            <div className="hidden sm:block p-3 sm:p-4">
                                                                <div className="overflow-x-auto">
                                                                    <table className="min-w-full text-sm">
                                                                        <thead>
                                                                            <tr className="text-xs text-gray-500 uppercase">
                                                                                <th className="text-left py-2 px-2 font-medium">Enquete</th>
                                                                                <th className="text-right py-2 px-2 font-medium">PPC</th>
                                                                                <th className="text-right py-2 px-2 font-medium border-l border-gray-300" colSpan="3">Lot Actuel</th>
                                                                                <th className="text-right py-2 px-2 font-medium border-l border-gray-300" colSpan="2">
                                                                                    Cumulé
                                                                                    {synthesisData.cumulative_batches_count > 0 && (
                                                                                        <span className="normal-case text-gray-400 ml-1">
                                                                                            ({synthesisData.cumulative_batches_count} lot{synthesisData.cumulative_batches_count > 1 ? 's' : ''})
                                                                                        </span>
                                                                                    )}
                                                                                </th>
                                                                                <th className="text-right py-2 px-2 font-medium border-l border-gray-300" colSpan="2">Total</th>
                                                                                <th className="text-right py-2 px-2 font-medium w-32 border-l border-gray-300">Budget</th>
                                                                            </tr>
                                                                            <tr className="text-xs text-gray-400 border-b border-gray-300">
                                                                                <th className="py-1 px-2"></th>
                                                                                <th className="py-1 px-2"></th>
                                                                                <th className="text-right py-1 px-2 border-l border-gray-300">Qte</th>
                                                                                <th className="text-right py-1 px-2">Montant</th>
                                                                                <th className="text-right py-1 px-2">Ajusté</th>
                                                                                <th className="text-right py-1 px-2 border-l border-gray-300">Qte</th>
                                                                                <th className="text-right py-1 px-2">Montant</th>
                                                                                <th className="text-right py-1 px-2 border-l border-gray-300">Qte</th>
                                                                                <th className="text-right py-1 px-2">Montant</th>
                                                                                <th className="py-1 px-2 border-l border-gray-300"></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-gray-200">
                                                                            {section.surveys.map((survey) => {
                                                                                const budgetColors = getBudgetColor(survey.grand_total.budget_percentage);
                                                                                return (
                                                                                    <tr key={survey.survey_id} className="hover:bg-white transition-colors">
                                                                                        <td className="py-2.5 px-2">
                                                                                            <div className="font-medium text-gray-900 text-sm">
                                                                                                {survey.survey_title}
                                                                                                {survey.client_name && <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{survey.client_name}</span>}
                                                                                            </div>
                                                                                            {survey.current_batch.excluded_amount > 0 && (
                                                                                                <div
                                                                                                    className="text-xs text-amber-600 mt-0.5 underline decoration-dotted cursor-pointer hover:text-amber-700"
                                                                                                    onClick={(e) => { e.stopPropagation(); setExcludedBreakdown(survey); }}
                                                                                                >
                                                                                                    {Number(survey.current_batch.excluded_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD {excludedAmountLabel(survey.current_batch.excluded_details)}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right text-gray-600">
                                                                                            {survey.payment_type === 'per_day'
                                                                                                ? <>{Number(survey.price_per_day).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}<span className="text-gray-400 text-xs ml-0.5">/jr</span></>
                                                                                                : Number(survey.price_per_complete).toLocaleString('fr-FR', { minimumFractionDigits: 2 })
                                                                                            }
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right font-medium text-gray-900 border-l border-gray-200">
                                                                                            {survey.payment_type === 'per_day' ? fmtDayQty(survey.current_batch.completes) : Number(survey.current_batch.completes).toLocaleString('fr-FR')}
                                                                                            {survey.payment_type !== 'per_day' && Number(survey.current_batch.excluded_completes || 0) > 0 && (
                                                                                                <div className="text-xs font-normal text-yellow-700" title="Complètes payées dans ce lot (hors retenue / exclusion), comptées dans le total">
                                                                                                    Ajusté : {(Number(survey.current_batch.completes) - Number(survey.current_batch.excluded_completes)).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className={`py-2.5 px-2 text-right font-medium text-gray-900 ${survey.current_batch.carryover_amount > 0 ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                                                                            onClick={() => survey.current_batch.carryover_amount > 0 && setMontantBreakdown(survey)}
                                                                                            title={survey.current_batch.carryover_amount > 0 ? 'Cliquer pour voir le détail' : undefined}
                                                                                        >
                                                                                            <span className={survey.current_batch.carryover_amount > 0 ? 'underline decoration-dotted decoration-gray-400' : ''}>
                                                                                                {Number(survey.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td
                                                                                            className={`py-2.5 px-2 text-right font-medium ${survey.current_batch.adjusted_amount !== survey.current_batch.amount ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                                                                            onClick={() => survey.current_batch.adjusted_amount !== survey.current_batch.amount && setAdjustmentBreakdown(survey)}
                                                                                            title={survey.current_batch.adjusted_amount !== survey.current_batch.amount ? 'Cliquer pour voir le détail' : undefined}
                                                                                        >
                                                                                            {survey.current_batch.adjusted_amount !== survey.current_batch.amount ? (
                                                                                                <span className={`underline decoration-dotted decoration-current ${survey.current_batch.adjusted_amount > survey.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}>
                                                                                                    {Number(survey.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right text-gray-600 border-l border-gray-200">
                                                                                            {survey.payment_type === 'per_day' ? fmtDayQty(survey.cumulative.completes) : Number(survey.cumulative.completes).toLocaleString('fr-FR')}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right text-gray-600">
                                                                                            {Number(survey.cumulative.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right font-semibold text-gray-900 border-l border-gray-200">
                                                                                            {survey.payment_type === 'per_day' ? fmtDayQty(survey.grand_total.completes) : Number(survey.grand_total.completes).toLocaleString('fr-FR')}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 text-right font-semibold text-gray-900">
                                                                                            {Number(survey.grand_total.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                        </td>
                                                                                        <td className="py-2.5 px-2 border-l border-gray-200">
                                                                                            {survey.total_budget > 0 ? (
                                                                                                <div className="flex flex-col items-end gap-1">
                                                                                                    <span className={`text-xs font-semibold ${budgetColors.text}`}>
                                                                                                        {survey.grand_total.budget_percentage}%
                                                                                                    </span>
                                                                                                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                                                                        <div
                                                                                                            className={`h-full rounded-full ${budgetColors.bar}`}
                                                                                                            style={{ width: `${Math.min(survey.grand_total.budget_percentage, 100)}%` }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <span className="text-xs text-gray-400">
                                                                                                        {Number(survey.total_budget).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                                    </span>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <span className="text-xs text-gray-400">-</span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                        <tfoot>
                                                                            <tr className="border-t-2 border-gray-300 font-bold text-gray-900">
                                                                                <td className="py-2.5 px-2">Total</td>
                                                                                <td className="py-2.5 px-2"></td>
                                                                                <td className="py-2.5 px-2 text-right border-l border-gray-200">
                                                                                    {(() => {
                                                                                        const qte = section.surveys.filter(sv => sv.payment_type !== 'per_day').reduce((s, sv) => s + sv.current_batch.completes, 0);
                                                                                        const days = section.surveys.filter(sv => sv.payment_type === 'per_day').reduce((s, sv) => s + sv.current_batch.completes, 0);
                                                                                        return (
                                                                                            <>
                                                                                                {qte > 0 && days > 0
                                                                                                    ? <div>{qte.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">+</span> {days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : qte > 0 ? <div>{qte.toLocaleString('fr-FR')}</div>
                                                                                                    : days > 0 ? <div>{days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : <div>0</div>
                                                                                                }
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right">
                                                                                    {section.surveys.reduce((s, sv) => s + sv.current_batch.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right">
                                                                                    {(() => {
                                                                                        const rawTotal = section.surveys.reduce((s, sv) => s + sv.current_batch.amount, 0);
                                                                                        const adjTotal = section.surveys.reduce((s, sv) => s + (sv.current_batch.adjusted_amount ?? sv.current_batch.amount), 0);
                                                                                        return adjTotal !== rawTotal ? (
                                                                                            <span className={adjTotal > rawTotal ? 'text-green-700' : 'text-red-700'}>
                                                                                                {adjTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                            </span>
                                                                                        ) : <span className="text-gray-400">-</span>;
                                                                                    })()}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right border-l border-gray-200">
                                                                                    {(() => {
                                                                                        const qte = section.surveys.filter(sv => sv.payment_type !== 'per_day').reduce((s, sv) => s + sv.cumulative.completes, 0);
                                                                                        const days = section.surveys.filter(sv => sv.payment_type === 'per_day').reduce((s, sv) => s + sv.cumulative.completes, 0);
                                                                                        return (
                                                                                            <>
                                                                                                {qte > 0 && days > 0
                                                                                                    ? <div>{qte.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">+</span> {days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : qte > 0 ? <div>{qte.toLocaleString('fr-FR')}</div>
                                                                                                    : days > 0 ? <div>{days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : <div>0</div>
                                                                                                }
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right">
                                                                                    {section.surveys.reduce((s, sv) => s + sv.cumulative.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right border-l border-gray-200">
                                                                                    {(() => {
                                                                                        const qte = section.surveys.filter(sv => sv.payment_type !== 'per_day').reduce((s, sv) => s + sv.grand_total.completes, 0);
                                                                                        const days = section.surveys.filter(sv => sv.payment_type === 'per_day').reduce((s, sv) => s + sv.grand_total.completes, 0);
                                                                                        return (
                                                                                            <>
                                                                                                {qte > 0 && days > 0
                                                                                                    ? <div>{qte.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">+</span> {days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : qte > 0 ? <div>{qte.toLocaleString('fr-FR')}</div>
                                                                                                    : days > 0 ? <div>{days.toLocaleString('fr-FR')} <span className="text-gray-400 text-xs font-normal">{days > 1 ? 'jrs' : 'jr'}</span></div>
                                                                                                    : <div>0</div>
                                                                                                }
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 text-right">
                                                                                    {section.surveys.reduce((s, sv) => s + sv.grand_total.amount, 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                                                                </td>
                                                                                <td className="py-2.5 px-2 border-l border-gray-200"></td>
                                                                            </tr>
                                                                        </tfoot>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Expanded but no surveys (non-controller) */}
                                                    {isExpanded && !section.is_controller && (!section.surveys || section.surveys.length === 0) && (
                                                        <div className="border-t border-gray-200 bg-gray-50 p-3 sm:p-4 text-sm text-gray-500 italic">
                                                            Pas de detail par enquete pour cette section
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Grand Total Row */}
                                        <div className="border border-gray-300 rounded-lg bg-gray-50 p-3 mt-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 sm:gap-3">
                                                    <span className="font-bold text-gray-900 text-sm sm:text-base">TOTAL</span>
                                                    <span className="text-xs sm:text-sm text-gray-600">
                                                        {synthesisData.totals.lines_count} ligne{synthesisData.totals.lines_count > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xs sm:text-sm font-bold text-gray-900 whitespace-nowrap">
                                                        {Number(synthesisData.totals.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                )}

                {/* Section Validation Status - Auto batches only */}
                {batch.creation_mode === 'auto' && batch.section_validation && batch.status === 'draft' && (
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-200">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                            Validation des Sections
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                            {[
                                { key: 'cati', label: 'CATI' },
                                { key: 'f2f', label: 'F2F' },
                                { key: 'controller', label: 'Contrôleurs' },
                            ].filter(s => batch.section_validation[s.key]).map(({ key, label }) => {
                                const sec = batch.section_validation[key];
                                const sectionLines = (batch.lines || []).filter(l => l.section === key);
                                const sectionNetTotal = sectionLines.filter(l => l.payment_status !== 'excluded' && !l.on_hold && !(l.payment_status === 'unpaid' && parseFloat(l.net || 0) < 200)).reduce((sum, l) => sum + (Number(l.net) || 0), 0);
                                const mySection = getUserSection();
                                const isSectionHead = ['resp_cati', 'chef_terrain', 'resp_controle'].includes(user?.role);
                                const isOtherSection = isSectionHead && mySection !== key;
                                // F2F: portion of the section being paid for UNCONTROLLED submissions the
                                // chef de terrain chose to pay (control.uncontrolled_to_pay). Flagged in red
                                // for the Head of Operations so it's visible before approving the section.
                                let f2fUncontrolledPaidCount = 0;
                                let f2fUncontrolledPaidAmount = 0;
                                if (key === 'f2f') {
                                    sectionLines.forEach(l => {
                                        if (!l.detail?.uses_submission_control) return;
                                        getSurveysArray(l).forEach(s => {
                                            const topay = s.control?.uncontrolled_to_pay || 0;
                                            if (topay > 0) {
                                                f2fUncontrolledPaidCount += topay;
                                                f2fUncontrolledPaidAmount += topay * (parseFloat(s.unit_price) || 0);
                                            }
                                        });
                                    });
                                }
                                return (
                                    <div
                                        key={key}
                                        className={`rounded-lg p-4 border-2 ${sec.validated ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-yellow-50'}`}
                                        onClick={() => {
                                            if (user?.role === 'chef_terrain_superieur') {
                                                setOpenSections({ cati: false, f2f: false, controller: false, [key]: true });
                                                if (linesTableRef.current) {
                                                    linesTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                            }
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium text-gray-900">{label}</span>
                                            {sec.validated ? (
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                            ) : (
                                                <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                                            )}
                                        </div>
                                        {!isOtherSection && (
                                        <div className="text-sm font-semibold text-gray-800 mb-2">
                                            Total net{user?.role === 'chef_terrain_superieur' && !sec.validated ? ' (provisoire)' : ''}: {sectionNetTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                                        </div>
                                        )}
                                        {key === 'f2f' && f2fUncontrolledPaidCount > 0 && (user?.role === 'chef_terrain_superieur' || isAdminViewer) && (
                                            <div className="text-xs font-semibold text-red-600 mb-2" title="Soumissions non contrôlées que le chef de terrain a choisi de payer (incluses dans le total net)">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                                                Dont non contrôlées payées : {f2fUncontrolledPaidCount} soumission{f2fUncontrolledPaidCount > 1 ? 's' : ''} ({f2fUncontrolledPaidAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD)
                                            </div>
                                        )}
                                        {batch?.creation_mode === 'express' ? null : sec.validated ? (
                                            <div className="text-xs text-green-700">
                                                Validée par {sec.validated_by_name || '-'}
                                                {sec.validated_at && (
                                                    <div>{new Date(sec.validated_at).toLocaleString('fr-FR')}</div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-yellow-700">En attente de validation</div>
                                        )}
                                        {sec.notes && (
                                            <div className="mt-2 text-xs text-gray-600 italic">{sec.notes}</div>
                                        )}
                                        {user?.role === 'chef_terrain_superieur' && sec.validated && (
                                            <button
                                                onClick={() => handleOpenSectionReject(key)}
                                                disabled={sectionProcessing}
                                                className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center"
                                            >
                                                <FontAwesomeIcon icon={faTimesCircle} className="mr-1.5" />
                                                Rejeter
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Section approve/recalculate/soldé buttons for section heads */}
                        {(() => {
                            const userSection = getUserSection();
                            if (!userSection || !batch.section_validation[userSection]) return null;
                            const sectionData = batch.section_validation[userSection];
                            if (sectionData.validated) return null;

                            // Check if section has any lines with net < 200 that are unpaid (soldé inactive)
                            const sectionLines = (batch.lines || []).filter(l => l.section === userSection);
                            const hasUnpaidLow = sectionLines.some(l => l.net < 200 && l.payment_status === 'unpaid');
                            const hasPendingLow = sectionLines.some(l => l.net < 200 && l.payment_status === 'pending');
                            const sectionSoldeActive = hasPendingLow || (!hasUnpaidLow && !hasPendingLow);

                            return (
                                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
                                    <button
                                        onClick={handleRecalculateSection}
                                        disabled={processing}
                                        className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm min-w-[170px]"
                                    >
                                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                                        Recalculer
                                    </button>
                                    {userSection === 'f2f' && batch.creation_mode === 'auto' && batch.status === 'draft' && (
                                        <button
                                            onClick={openBulkRetenueModal}
                                            disabled={processing}
                                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm min-w-[170px]"
                                            title="Appliquer une retenue (%) sur une ou plusieurs enquêtes à toutes les lignes F2F concernées"
                                        >
                                            <FontAwesomeIcon icon={faPercent} className="mr-2" />
                                            Retenue en masse
                                        </button>
                                    )}
                                    <button
                                        onClick={handleToggleSectionSolde}
                                        disabled={processing}
                                        className={`relative px-5 py-2.5 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 flex items-center justify-center text-sm min-w-[170px] ${
                                            hasUnpaidLow
                                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                                : 'bg-red-700 hover:bg-red-800 text-white shadow-md'
                                        }`}
                                        title={hasUnpaidLow
                                            ? 'Cliquer pour activer le mode "Soldé" - Les montants < 200 MAD seront inclus'
                                            : 'Cliquer pour désactiver le mode "Soldé" - Les montants < 200 MAD seront exclus'
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
                                        Soldé
                                        {!hasUnpaidLow && sectionLines.some(l => l.net < 200) ? (
                                            <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full border border-white"></span>
                                        ) : null}
                                    </button>
                                    <button
                                        onClick={handleValidateSection}
                                        disabled={sectionProcessing}
                                        className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm min-w-[170px]"
                                    >
                                        {sectionProcessing ? (
                                            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        ) : (
                                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                        )}
                                        Valider ma section
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Lock blocked message for chef_terrain_superieur */}
                        {user?.role === 'chef_terrain_superieur' && !batch.all_sections_validated && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center text-sm text-yellow-700 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 flex-shrink-0" />
                                    <span>Le verrouillage sera disponible une fois toutes les sections validées.</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons - Only show if there are buttons to display */}
                {!(isAdminViewer && batch.status === 'draft') && (batch.canEdit || batch.canLock || batch.canValidate || batch.canExport || (batch.status === 'exported' && (user?.role === 'resp_admin' || user?.role === 'chef_terrain_superieur'))) && (
                    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-200">
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                            {batch.canEdit && user?.role === 'chef_terrain_superieur' && batch.creation_mode !== 'auto' && (
                                <>
                                    <button
                                        onClick={handleRecalculate}
                                        disabled={processing}
                                        className="bg-gray-800 hover:bg-gray-900 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto min-w-[140px]"
                                    >
                                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                                        Recalculer
                                    </button>
                                    <button
                                        onClick={handleToggleSolde}
                                        disabled={processing}
                                        className={`relative px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto min-w-[140px] ${
                                            batch.force_include_low_amounts
                                                ? 'bg-red-700 hover:bg-red-800 text-white shadow-md'
                                                : 'bg-red-600 hover:bg-red-700 text-white'
                                        }`}
                                        title={batch.force_include_low_amounts
                                            ? 'Cliquer pour désactiver le mode "Soldé" - Les montants < 200 MAD ne seront plus inclus dans les exports'
                                            : 'Cliquer pour activer le mode "Soldé" - Les montants < 200 MAD seront inclus dans les exports'
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
                                        Soldé
                                        {batch.force_include_low_amounts ? (
                                            <span className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full border border-white"></span>
                                        ) : null}
                                    </button>
                                </>
                            )}
                            {batch.canLock && user?.role === 'chef_terrain_superieur' && (
                                <button
                                    onClick={handleLock}
                                    disabled={processing}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto min-w-[140px]"
                                >
                                    <FontAwesomeIcon icon={faLock} className="mr-2" />
                                    Verrouiller
                                </button>
                            )}
                            {batch.canValidate && (
                                <>
                                    <button
                                        onClick={handleValidate}
                                        disabled={processing}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
                                    >
                                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                        Valider
                                    </button>
                                    {user?.role !== 'resp_admin' && user?.role !== 'chef_terrain_superieur' && (
                                        <button
                                            onClick={() => setShowRejectModal(true)}
                                            disabled={processing}
                                            className="bg-gray-800 hover:bg-gray-900 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
                                        >
                                            <FontAwesomeIcon icon={faUndo} className="mr-2" />
                                            Retourner pour révision
                                        </button>
                                    )}
                                </>
                            )}
                            {batch.canExport && (user?.role === 'resp_admin' || user?.role === 'chef_terrain_superieur' || user?.role === 'administrateur_general') && (
                                <button
                                    onClick={handleExport}
                                    disabled={processing}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 flex items-center justify-center text-sm sm:text-base shadow-lg w-full sm:w-auto"
                                >
                                    <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                                    Exporter
                                </button>
                            )}
                            {batch.status === 'exported' && user?.role === 'resp_admin' && (
                                <button
                                    onClick={() => navigate(`/payments/${id}/confirm`)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
                                >
                                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                    Confirmer Paiements
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Payment Completion Section */}
                {completionData && completionData.lines && completionData.lines.length > 0 && (
                    <div ref={completionSectionRef} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 mb-6">
                        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-green-50">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                                <h3 onClick={() => setShowCompletionTable(v => !v)}
                                    className="text-base sm:text-lg font-semibold text-gray-900 cursor-pointer select-none">
                                    Completion des Paiements
                                </h3>
                                <div className="flex items-center gap-3">
                                    {completionData.lines.length > 0 && (
                                        <button
                                            onClick={handleExportCompletion}
                                            disabled={exportingCompletion}
                                            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-50 text-sm sm:text-base whitespace-nowrap w-full sm:w-auto"
                                        >
                                            <FontAwesomeIcon
                                                icon={exportingCompletion ? faSpinner : faDownload}
                                                spin={exportingCompletion}
                                                className="mr-2"
                                            />
                                            {exportingCompletion ? 'Export...' : 'Exporter Completion'}
                                        </button>
                                    )}
                                    <FontAwesomeIcon
                                        onClick={() => setShowCompletionTable(v => !v)}
                                        icon={showCompletionTable ? faChevronUp : faChevronDown}
                                        className="text-gray-500 cursor-pointer flex-shrink-0"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Completion Stats */}
                        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                            {(() => {
                                const stats = getStatusStats(completionData.lines);
                                return (
                                    <div className="flex flex-wrap gap-3 sm:gap-4">
                                        {stats.paid > 0 && (
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {stats.paid} Payé{stats.paid > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        {stats.unpaid > 0 && (
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {stats.unpaid} Non payé{stats.unpaid > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        {stats.rejected > 0 && (
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {stats.rejected} Annulé{stats.rejected > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        {stats.insufficient > 0 && (
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faQuestionCircle} className="text-orange-600 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {stats.insufficient} Insuffisant{stats.insufficient > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        {stats.excluded > 0 && (
                                            <div className="flex items-center">
                                                <FontAwesomeIcon icon={faTimesCircle} className="text-amber-600 mr-2" />
                                                <span className="text-sm font-medium text-gray-900">
                                                    {stats.excluded} Exclu{stats.excluded > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        {(completionData.lines.filter(l => l.is_partially_excluded).length > 0) && (
                                            <div className="flex items-center">
                                                <span className="text-sm font-medium text-amber-800 mr-2">Partiel</span>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {completionData.lines.filter(l => l.is_partially_excluded).length} ligne{completionData.lines.filter(l => l.is_partially_excluded).length > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        )}
                                        <div className="text-xs sm:text-sm text-gray-500 w-full sm:w-auto sm:ml-auto mt-2 sm:mt-0">
                                            Confirmé le: {completionData.confirmed_at ? new Date(completionData.confirmed_at).toLocaleString('fr-FR') : '-'}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Completion Lines Table — hidden until the header is clicked */}
                        {showCompletionTable && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Personne
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Bénéficiaire
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Montant
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Méthode
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Statut Final
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Preuves
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Confirmé le
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {completionData.lines.map((line) => (
                                        <tr key={line.id}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                                                    <div>
                                                        <button
                                                            onClick={() => navigate(`/payments/${id}/lines/${line.id}`)}
                                                            title="Voir le détail de la ligne"
                                                            className="text-sm font-medium text-green-600 hover:text-green-800 hover:underline text-left"
                                                        >
                                                            {line.person_name}
                                                        </button>
                                                        <div className="text-xs text-gray-400">
                                                            Ref: {line.person_ref}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {line.payment_name || '-'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {parseFloat(line.net).toFixed(2)} MAD
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {line.payment_method_type === 'cash' ? 'Espèces' : 
                                                     line.payment_method_type === 'bank' ? 'Banque' : 'Transfert'}
                                                </span>
                                                <div className="text-xs text-gray-500">
                                                    {line.payment_method_type === 'cash' ? 'Paiement en espèces à Immersion' : 
                                                     (line.bank_name || line.transfer_service)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {getCompletionStatusBadge(line.payment_status)}
                                                    {line.is_partially_excluded && (
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800" title="Enquête(s) partiellement exclue(s)">
                                                            Partiel
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {line.proof_files && line.proof_files.length > 0 ? (
                                                    <div className="flex flex-col gap-2">
                                                        {line.proof_files.map((file) => (
                                                            <button
                                                                key={file.id}
                                                                onClick={() => handleDownloadProofFile(file.id, file.original_filename)}
                                                                title={file.original_filename}
                                                                className="inline-flex items-center max-w-[200px] px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 text-xs font-medium rounded transition-colors duration-200"
                                                            >
                                                                <FontAwesomeIcon icon={faDownload} className="mr-2 flex-shrink-0 text-gray-500" />
                                                                <span className="truncate min-w-0">{file.original_filename}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {line.confirmed_at ? new Date(line.confirmed_at).toLocaleString('fr-FR') : '-'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {line.payment_notes ? (() => {
                                                    const redNote = line.payment_status === 'unpaid' &&
                                                        (line.payment_notes.includes('marqué automatiquement') || line.payment_notes.includes('Montant net inférieur à 200'));
                                                    return (
                                                        <div className="flex flex-col">
                                                            <button
                                                                type="button"
                                                                onClick={() => setNoteModal(line.payment_notes)}
                                                                title="Cliquer pour voir la note complète"
                                                                className={`text-sm text-left truncate max-w-[150px] hover:underline ${redNote ? 'text-red-700 font-medium' : 'text-gray-900'}`}
                                                            >
                                                                {line.payment_notes}
                                                            </button>
                                                            {redNote && (
                                                                <span className="text-xs text-red-600 mt-1 font-semibold">
                                                                    Exclu du total net
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })() : (
                                                    <span className="text-sm text-gray-400">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                )}

                {/* Exports Section */}
                {batch.exports && batch.exports.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
                        <div onClick={() => setShowExports(v => !v)}
                            className="flex items-center justify-between mb-4 cursor-pointer select-none">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <FontAwesomeIcon icon={faFileExport} className="text-red-600" />
                                Fichiers Exportés
                            </h3>
                            <FontAwesomeIcon icon={showExports ? faChevronUp : faChevronDown} className="text-gray-500" />
                        </div>
                        {showExports && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {batch.exports.map((exp) => (
                                <div key={exp.id} className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                                    exp.export_type === 'ALL' 
                                        ? 'border-blue-300 bg-blue-50' 
                                        : 'border-gray-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`font-medium ${
                                            exp.export_type === 'ALL' 
                                                ? 'text-blue-900' 
                                                : 'text-gray-900'
                                        }`}>
                                            {exp.export_type === 'ALL' ? 'Export Complet' : exp.export_type}
                                        </span>
                                        <button
                                            onClick={() => handleDownloadExport(exp.id, exp.export_type)}
                                            className="text-red-600 hover:text-red-700"
                                            disabled={processing}
                                        >
                                            <FontAwesomeIcon icon={processing ? faSpinner : faDownload} spin={processing} />
                                        </button>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <div>{exp.record_count} enregistrement(s)</div>
                                        <div className="font-semibold text-green-600">
                                            {parseFloat(exp.total_amount).toFixed(2)} MAD
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {new Date(exp.exported_at).toLocaleString('fr-FR')}
                                        </div>
                                        {exp.export_type === 'ALL' && (
                                            <div className="text-xs text-blue-600 mt-1 font-medium">
                                                Toutes les méthodes de paiement
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                )}

                {/* Rejection Notes Section - filtered by role */}
                {(() => {
                    const userRole = user?.role;
                    const userSection = getUserSection();
                    const filteredRejections = (batch.rejection_notes || []).filter(rejection => {
                        if (rejection.section) {
                            // Section rejection: show to the concerned section head + chef_terrain_superieur
                            if (userRole === 'chef_terrain_superieur') return true;
                            const sectionRoleMap = { cati: 'resp_cati', f2f: 'chef_terrain', controller: 'resp_controle' };
                            return sectionRoleMap[rejection.section] === userRole;
                        } else {
                            // Batch-level rejection: only show to chef_terrain_superieur
                            return userRole === 'chef_terrain_superieur';
                        }
                    });

                    if (filteredRejections.length === 0) return null;

                    return (
                        <div className="bg-white border border-red-300 rounded-lg shadow-sm mb-6">
                            <div
                                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => setShowRejectionHistory(!showRejectionHistory)}
                            >
                                <h3 className="text-base sm:text-lg font-semibold text-red-700 flex items-center justify-between">
                                    <div className="flex items-center flex-wrap gap-2">
                                        <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-red-600" />
                                        <span className="break-words">Historique des retours pour révision</span>
                                        <span className="text-xs sm:text-sm bg-red-100 text-red-700 px-2 py-1 rounded-full whitespace-nowrap">
                                            {filteredRejections.length}
                                        </span>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={showRejectionHistory ? faChevronUp : faChevronDown}
                                        className="text-red-600 flex-shrink-0 ml-2"
                                    />
                                </h3>
                            </div>

                            {showRejectionHistory && (
                                <div className="px-4 pb-4 space-y-4">
                                    {filteredRejections.map((rejection, index) => (
                                        <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 space-y-2 sm:space-y-0">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    <FontAwesomeIcon icon={faUser} className="mr-2 text-red-600" />
                                                    <span className="font-medium text-gray-900">{rejection.rejected_by_name}</span>
                                                    <span className="mx-2 text-red-400">•</span>
                                                    <span className="text-red-600 font-medium">{{ administrateur_general_superieur: 'Business Officer', administrateur_general: 'Administrateur Général', resp_admin: 'Resp. Administratif', chef_terrain_superieur: 'Head of Operations', chef_terrain: 'Chef de Terrain', resp_cati: 'Resp. CATI', resp_controle: 'Resp. Contrôle', charge_etude_superieur: 'Chargé d\'Étude Supérieur', charge_etude: 'Chargé d\'Étude' }[rejection.rejected_by_role] || rejection.rejected_by_role}</span>
                                                    {rejection.section && (
                                                        <>
                                                            <span className="mx-2 text-red-400">•</span>
                                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                                                Section {{ cati: 'CATI', f2f: 'F2F', controller: 'Contrôleurs' }[rejection.section]}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex items-center text-sm text-red-500">
                                                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                                                    <span className="text-xs sm:text-sm">
                                                        {new Date(rejection.rejected_at).toLocaleString('fr-FR')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="bg-white border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
                                                {rejection.note && rejection.note.trim() ? (
                                                    <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">{rejection.note}</p>
                                                ) : (
                                                    <p className="text-gray-400 italic text-sm sm:text-base">
                                                        {rejection.rejected_by_name || 'Utilisateur'} n'a pas indiqué de raison
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}


                {/* Payment Lines Table */}
                <div ref={linesTableRef} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                    {(() => {
                        const isSectionHead = ['resp_cati', 'chef_terrain', 'resp_controle'].includes(user?.role);
                        const userSection = getUserSection();
                        return (
                    <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Détails de Paiement ({batch.lines?.length || 0})
                        </h3>
                        {!isSectionHead && (
                        <div className="section-segment-group">
                            {['cati', 'f2f', 'controller']
                                .filter(key => (batch.lines || []).filter(l => l.section === key).length > 0)
                                .map(key => {
                                    const labelMap = { cati: 'CATI', f2f: 'F2F', controller: 'Contrôle' };
                                    const count = (batch.lines || []).filter(l => l.section === key).length;
                                    const isOpen = openSections[key];
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setOpenSections(prev => {
                                            const wasOpen = prev[key];
                                            if (wasOpen) {
                                                return { cati: false, f2f: false, controller: false };
                                            }
                                            return { cati: false, f2f: false, controller: false, [key]: true };
                                        })}
                                            className={`section-segment ${isOpen ? 'section-segment--checked' : ''}`}
                                        >
                                            {labelMap[key]} ({count})
                                        </button>
                                    );
                                })}
                        </div>
                        )}
                    </div>
                        );
                    })()}
                    <div className="overflow-x-auto">
                        {(() => {
                            const isSectionHead = ['resp_cati', 'chef_terrain', 'resp_controle'].includes(user?.role);
                            const userSection = getUserSection();
                            const anyOpen = isSectionHead && userSection
                                ? true
                                : (openSections.cati || openSections.f2f || openSections.controller);
                            if (!anyOpen) {
                                return (
                                    <div className="px-6 py-6 text-sm text-gray-500 border-t border-gray-100">
                                        Sélectionnez une section (CATI, F2F ou Contrôle) pour afficher les lignes.
                                    </div>
                                );
                            }

                            const filteredLines = isSectionHead && userSection
                                ? (batch.lines || []).filter(line => !line.section || line.section === userSection)
                                : (batch.lines || []).filter(line => {
                                    if (!line.section) return true;
                                    return openSections[line.section] === true;
                                });

                            if (filteredLines.length === 0) {
                                return (
                                    <div className="px-6 py-6 text-sm text-gray-500 border-t border-gray-100">
                                        Aucune ligne de paiement trouvée pour les sections sélectionnées.
                                    </div>
                                );
                            }

                            return (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Personne
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Quantité
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Prix Unit.
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ajust.
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Net
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Paiement
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Statut
                                            </th>
                                            {!isAdminViewer && !(batch.creation_mode === 'express' && user?.role === 'chef_terrain') && !(batch.creation_mode === 'auto' && getUserSection() && batch.section_validation?.[getUserSection()]?.validated) && ((batch.canEdit && !(batch.creation_mode === 'auto' && user?.role === 'chef_terrain_superieur')) || (batch.creation_mode === 'auto' && batch.status === 'draft' && getUserSection() && batch.section_validation?.[getUserSection()] && !batch.section_validation[getUserSection()].validated)) && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredLines.map((line) => {
                                        const isAutoUnpaid = line.payment_status === 'unpaid' &&
                                                             line.payment_notes &&
                                                             (line.payment_notes.includes('marqué automatiquement') || line.payment_notes.includes('Montant net inférieur à 200'));
                                        const isExcluded = isLineExcluded(line);
                                        const isFrozen = isLineFrozen(line);
                                        const isPartialExcluded = isLinePartiallyExcluded(line);
                                        // Left-border colours use !border-l-*: the tbody's divide-gray-200 sets border-color on every
                                        // row but the first with a more specific selector, so a plain border-* only showed on row 1.
                                        return (
                                        <tr key={line.id} className={
                                            isFrozen ? 'bg-sky-50 border-l-4 !border-l-sky-500 opacity-75' :
                                            isExcluded ? 'bg-amber-50 border-l-4 !border-l-amber-500 opacity-75' :
                                            isPartialExcluded && getFullyExcludedSurveyIds(line).length === 0 ? 'border-l-4 [border-left-style:dashed] !border-l-yellow-400' :
                                            isPartialExcluded ? 'bg-amber-50/50 border-l-4 !border-l-amber-300' :
                                            line.on_hold ? 'bg-orange-50' :
                                            isAutoUnpaid ? 'bg-red-50 border-l-4 !border-l-red-400' :
                                            ''
                                        }>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="font-medium text-gray-900">{line.person_name}</div>
                                                    {line.section === 'cati' && line.person_poste_number != null && line.person_poste_number !== '' && (
                                                        <div className="text-xs text-gray-500">Poste n°{line.person_poste_number}</div>
                                                    )}
                                                    <div className="text-sm text-gray-500">
                                                        <FontAwesomeIcon icon={faIdCard} className="mr-1" />
                                                        {line.person_ref}
                                                    </div>
                                                    {isFrozen && (
                                                        <div className="text-xs text-sky-700 mt-1">
                                                            <FontAwesomeIcon icon={faSnowflake} className="mr-1" />
                                                            Gelé: {line.freeze_reason}
                                                            {line.freeze_requested_by_name && (
                                                                <span className="text-sky-500"> (par {line.freeze_requested_by_name}, {line.freeze_requested_at ? new Date(line.freeze_requested_at).toLocaleDateString('fr-FR') : ''})</span>
                                                            )}
                                                            <div className="text-sky-500">Effectif à la confirmation du lot. Revient automatiquement si la personne retravaille.</div>
                                                        </div>
                                                    )}
                                                    {parseFloat(line.auto_unfrozen_amount || 0) !== 0 && (
                                                        <div className="text-xs text-sky-700 mt-1" title="Montant gelé réintégré automatiquement : la personne a de nouveau travaillé. Le dégel sera effectif à la confirmation du lot.">
                                                            <FontAwesomeIcon icon={faSnowflake} className="mr-1" />
                                                            Inclut {Number(line.auto_unfrozen_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD dégelés (reprise d'activité)
                                                        </div>
                                                    )}
                                                    {isExcluded && !isFrozen && (
                                                        <div className="text-xs text-amber-700 mt-1">
                                                            <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                            Exclu: {line.exclusion_reason}
                                                            {line.excluded_by_name && (
                                                                <span className="text-amber-500"> (par {line.excluded_by_name}, {line.excluded_at ? new Date(line.excluded_at).toLocaleDateString('fr-FR') : ''})</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {isPartialExcluded && !isExcluded && getExcludedSurveyNames(line).length > 0 && (
                                                        <div className="text-xs text-amber-600 mt-1">
                                                            <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                            Enquête{getExcludedSurveyNames(line).length > 1 ? 's' : ''} exclue{getExcludedSurveyNames(line).length > 1 ? 's' : ''} : {getExcludedSurveyNames(line).join(', ')}
                                                        </div>
                                                    )}
                                                    {!isExcluded && getRetenues(line).length > 0 && (
                                                        <div className="text-xs text-yellow-700 mt-1">
                                                            <FontAwesomeIcon icon={faPercent} className="mr-1" />
                                                            Retenue : {getRetenues(line).map(r => `${formatPct(r.percentage)} % ${r.survey_title || ''} (${Math.abs(r.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD)`).join(', ')}
                                                            <span className="text-yellow-600"> — gardé en garantie</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 text-sm text-gray-900 ${line.person_type === 'supervisor' && (getSurveysArray(line).some(s => s.interviewers?.length > 0) || line.detail?.uses_submission_control) ? 'cursor-pointer hover:bg-red-50 transition-colors' : ''}`}
                                                onClick={() => {
                                                    if (line.person_type === 'supervisor' && (getSurveysArray(line).some(s => s.interviewers?.length > 0) || line.detail?.uses_submission_control)) {
                                                        setInterviewerBreakdownLine(line);
                                                    }
                                                }}
                                                title={line.person_type === 'supervisor' && (getSurveysArray(line).some(s => s.interviewers?.length > 0) || line.detail?.uses_submission_control) ? 'Cliquer pour voir le détail par enquêteur' : undefined}
                                            >
                                                {line.person_type === 'controller' ? (
                                                    // Display for controllers: show full days and half days
                                                    <div className="space-y-1">
                                                        {line.detail?.full_days > 0 && (
                                                            <div className="text-xs">
                                                                <div className="font-medium text-gray-700">
                                                                    {line.detail.full_days} jour{line.detail.full_days > 1 ? 's' : ''}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {line.detail?.half_days > 0 && (
                                                            <div className="text-xs">
                                                                <div className="font-medium text-gray-700">
                                                                    {line.detail.half_days} demi-journée{line.detail.half_days > 1 ? 's' : ''}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {(!line.detail?.full_days || line.detail.full_days === 0) && 
                                                         (!line.detail?.half_days || line.detail.half_days === 0) && (
                                                            <div className="text-xs text-gray-500">
                                                                {line.qty} jour{line.qty > 1 ? 's' : ''} (total)
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (() => {
                                                    // Calculate original values before deductions
                                                    const { originalQty } = getOriginalValues(line);
                                                    
                                                    // Filter out carryover-only surveys and calculate current batch quantities
                                                    const allSurveys = getSurveysArray(line);
                                                    const currentWeekSurveys = allSurveys
                                                        .filter(s => !s.is_carryover_only)
                                                        .map(survey => {
                                                            // survey.qty is actual productivity (after deductions in backend)
                                                            // survey.carryover_qty is the carryover portion
                                                            // Show actual current batch work, not inflated by deductions
                                                            const surveyQty = parseFloat(survey.qty) || 0;
                                                            const carryoverQty = parseFloat(survey.carryover_qty) || 0;

                                                            const currentBatchQty = surveyQty - carryoverQty;

                                                            return {
                                                                ...survey,
                                                                currentBatchQty: Math.max(0, currentBatchQty)
                                                            };
                                                        })
                                                        .filter(s => s.currentBatchQty > 0); // Only show surveys with current batch work
                                                    
                                                    // Per-day awareness: derive subtotals (set by backend for CATI lines).
                                                    const subPerDay = parseFloat(line.detail?.subtotals?.per_day?.qty || 0);
                                                    const subPerComplete = parseFloat(line.detail?.subtotals?.per_complete?.qty || 0);
                                                    const isMixedLine = subPerDay > 0 && subPerComplete > 0;
                                                    const fmtSurveyQty = (s) => {
                                                        const qty = s.currentBatchQty;
                                                        if (s.payment_type === 'per_day') {
                                                            return fmtDayQty(qty);
                                                        }
                                                        return `${qty}`;
                                                    };

                                                    // F2F controlled-payment: uncontrolled submissions NOT included in the payment (deferred)
                                                    const f2fCtrlSurveys = line.detail?.uses_submission_control ? getSurveysArray(line).filter(s => s.control) : [];
                                                    const f2fDeferred = f2fCtrlSurveys.reduce((sum, s) => sum + (s.control?.deferred || 0), 0);
                                                    const f2fDeferredSurveys = f2fCtrlSurveys.filter(s => (s.control?.deferred || 0) > 0);
                                                    const f2fStar = f2fDeferred > 0 ? (
                                                        <span title={`${f2fDeferred} non contrôlée(s) non incluse(s) dans ce paiement`} className="text-red-600 font-bold ml-0.5">*</span>
                                                    ) : null;
                                                    // Per-survey breakdown so a survey with only uncontrolled work is still visible
                                                    // in the quantity column (not just inside the modal).
                                                    const f2fNote = f2fDeferredSurveys.length > 0 ? (
                                                        <div className="text-red-600 text-[10px] mt-0.5 space-y-0.5">
                                                            {f2fDeferredSurveys.map((s, i) => (
                                                                <div key={i}>
                                                                    <span className="font-bold">{s.survey_title || ('Enquête #' + s.survey_id)}: </span>
                                                                    - {s.control.deferred} non contrôlée{s.control.deferred > 1 ? 's' : ''}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null;

                                                    return currentWeekSurveys.length > 1 ? (
                                                        <div className="space-y-1">
                                                            {currentWeekSurveys.map((survey, idx) => (
                                                                <div key={idx} className="text-xs">
                                                                    <div className="font-medium text-gray-700">{fmtSurveyQty(survey)}</div>
                                                                    <div className="text-gray-500 text-[10px]">{survey.survey_title}</div>
                                                                </div>
                                                            ))}
                                                            <div className="pt-1 mt-1 border-t border-gray-200">
                                                                {isMixedLine ? (
                                                                    <>
                                                                        <div className="font-semibold text-gray-900">{subPerComplete}</div>
                                                                        <div className="text-gray-500 text-[10px]">Total complétions</div>
                                                                        <div className="font-semibold text-gray-900 mt-1">{subPerDay} jour{subPerDay > 1 ? 's' : ''}</div>
                                                                        <div className="text-gray-500 text-[10px]">Total jours</div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="font-semibold text-gray-900">
                                                                            {subPerDay > 0 ? `${subPerDay} jour${subPerDay > 1 ? 's' : ''}` : originalQty}{f2fStar}
                                                                        </div>
                                                                        <div className="text-gray-500 text-[10px]">Total</div>
                                                                    </>
                                                                )}
                                                                {f2fNote}
                                                            </div>
                                                        </div>
                                                    ) : currentWeekSurveys.length === 1 ? (
                                                        <div>
                                                            <div className="flex items-center space-x-2">
                                                                <span>{fmtSurveyQty(currentWeekSurveys[0])}{f2fStar}</span>
                                                                {line.detail?.is_carryover_only && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                        Report uniquement
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {currentWeekSurveys[0].survey_title && (
                                                                <div className="text-gray-500 text-[10px]">{currentWeekSurveys[0].survey_title}</div>
                                                            )}
                                                            {f2fNote}
                                                        </div>
                                                    ) : (
                                                        // No current week work, only carryover or advance
                                                        <div>
                                                            <div className="flex items-center space-x-2">
                                                                <span>{line.qty}{f2fStar}</span>
                                                                {line.detail?.is_carryover_only && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                        Report uniquement
                                                                    </span>
                                                                )}
                                                                {line.detail?.is_advance_only && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                        Avance uniquement
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {allSurveys.length >= 1 && allSurveys[0].survey_title && (
                                                                <div className="text-gray-500 text-[10px]">{allSurveys.map(s => s.survey_title).join(', ')}</div>
                                                            )}
                                                            {f2fNote}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {(() => {
                                                    // Filter out carryover-only surveys and calculate current batch quantities
                                                    const allSurveys = getSurveysArray(line);
                                                    const currentWeekSurveys = allSurveys
                                                        .filter(s => !s.is_carryover_only && parseFloat(s.unit_price) > 0)
                                                        .map(survey => ({
                                                            ...survey,
                                                            // Subtract carryover quantity to show only current batch quantity
                                                            currentBatchQty: Math.max(0, (parseFloat(survey.qty) || 0) - (parseFloat(survey.carryover_qty) || 0))
                                                        }))
                                                        .filter(s => s.currentBatchQty > 0); // Only show surveys with current batch work
                                                    
                                                    // Mixed-type lines: show one weighted average per payment_type, stacked.
                                                    const subPerDayAmt = parseFloat(line.detail?.subtotals?.per_day?.amount || 0);
                                                    const subPerDayQty = parseFloat(line.detail?.subtotals?.per_day?.qty || 0);
                                                    const subPerCompleteAmt = parseFloat(line.detail?.subtotals?.per_complete?.amount || 0);
                                                    const subPerCompleteQty = parseFloat(line.detail?.subtotals?.per_complete?.qty || 0);
                                                    if (subPerDayQty > 0 && subPerCompleteQty > 0) {
                                                        const avgComplete = subPerCompleteAmt / subPerCompleteQty;
                                                        const avgDay = subPerDayAmt / subPerDayQty;
                                                        return (
                                                            <div className="space-y-1">
                                                                <div className="text-xs">
                                                                    <span className="font-medium">{avgComplete.toFixed(2)} MAD</span>
                                                                    <span className="text-gray-500 text-[10px] ml-1">/complète</span>
                                                                </div>
                                                                <div className="text-xs">
                                                                    <span className="font-medium">{avgDay.toFixed(2)} MAD</span>
                                                                    <span className="text-gray-500 text-[10px] ml-1">/jour</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    if (currentWeekSurveys.length > 1) {
                                                        // Multiple surveys (same type): show weighted average.
                                                        const totalQty = currentWeekSurveys.reduce((sum, s) => sum + s.currentBatchQty, 0);
                                                        const totalAmount = currentWeekSurveys.reduce((sum, s) => sum + (s.currentBatchQty * parseFloat(s.unit_price)), 0);
                                                        const avgPrice = totalQty > 0 ? totalAmount / totalQty : 0;
                                                        const suffix = subPerDayQty > 0 ? ' /jour' : '';
                                                        return <span>{avgPrice.toFixed(2)} MAD{suffix}</span>;
                                                    } else if (currentWeekSurveys.length === 1) {
                                                        const s = currentWeekSurveys[0];
                                                        const suffix = s.payment_type === 'per_day' ? ' /jour' : '';
                                                        return <span>{parseFloat(s.unit_price).toFixed(2)} MAD{suffix}</span>;
                                                    } else if (line.detail?.is_carryover_only) {
                                                        return <span className="text-gray-400 text-xs">N/A</span>;
                                                    } else {
                                                        return <span>{parseFloat(line.unit_price).toFixed(2)} MAD</span>;
                                                    }
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {(() => {
                                                    // Calculate original total before deductions
                                                    const { originalTotal } = getOriginalValues(line);
                                                    return originalTotal.toFixed(2) + ' MAD';
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {/* Retenue de garantie already held for this supervisor: information only, not in the net */}
                                                {(() => {
                                                    const held = Object.entries(line.retenue_held || {});
                                                    const total = held.reduce((s, [, h]) => s + parseFloat(h.amount || 0), 0);
                                                    if (total <= 0) return null;
                                                    const title = 'Garantie cumulée (non payable, payée quand le travail est marqué terminé) : '
                                                        + held.map(([sid, h]) => `${getSurveysArray(line).find(s => String(s.survey_id) === String(sid))?.survey_title || batch?.surveys?.find(s => String(s.id) === String(sid))?.title || `Enquête #${sid}`} ${parseFloat(h.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD`).join(', ');
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => setHeldDetailLine(line)}
                                                            className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-yellow-300 bg-white text-xs text-gray-600 hover:bg-yellow-50 hover:underline"
                                                            title={title}
                                                        >
                                                            <FontAwesomeIcon icon={faPercent} className="text-yellow-600 text-[10px]" />
                                                            Garantie cumulée {total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                                        </button>
                                                    );
                                                })()}
                                                {line.adjustments && line.adjustments.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {line.adjustments.map((adj, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleAdjustmentClick(adj, line)}
                                                                className={`block font-medium hover:underline transition-colors duration-200 ${
                                                                    adj.kind === 'retenue' ? 'text-yellow-700 hover:text-yellow-800'
                                                                    : adj.amount > 0 ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'
                                                                }`}
                                                                title={adj.kind === 'retenue' ? `Retenue de garantie ${formatPct(adj.percentage)} % — gardée jusqu'à la fin du travail` : undefined}
                                                            >
                                                                {adj.amount > 0 ? '+' : ''}{adj.amount} MAD
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : Object.keys(line.retenue_held || {}).length === 0 ? (
                                                    <span className="text-gray-400">-</span>
                                                ) : null}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {parseFloat(line.net).toFixed(2)} MAD
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div>
                                                    <div className="font-medium">
                                                        {line.payment_method_type === 'cash' ? 'Espèces' : 
                                                         line.payment_method_type === 'bank' ? 'Banque' : 'Transfert'}
                                                    </div>
                                                    {line.payment_method_type === 'cash' ? (
                                                        <div className="text-xs text-gray-500">
                                                       
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-gray-500">
                                                            {line.bank_name || line.transfer_service}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {isFrozen ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700" title="Gel effectif à la confirmation du lot">
                                                        <FontAwesomeIcon icon={faSnowflake} className="mr-1" />
                                                        Gelé
                                                    </span>
                                                ) : isExcluded ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                        <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                        Exclu
                                                    </span>
                                                ) : isPartialExcluded && getFullyExcludedSurveyIds(line).length === 0 ? (
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-yellow-400 text-yellow-700"
                                                        title={getRetenues(line).map(r => `${formatPct(r.percentage)} % ${r.survey_title || ''}`).join(', ')}
                                                    >
                                                        <FontAwesomeIcon icon={faPercent} className="mr-1" />
                                                        Retenue ({getRetenues(line).length})
                                                    </span>
                                                ) : isPartialExcluded ? (
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
                                                        title={[
                                                            getExcludedSurveyNames(line).length ? `Exclues : ${getExcludedSurveyNames(line).join(', ')}` : null,
                                                            getRetenues(line).length ? `Retenues : ${getRetenues(line).map(r => `${formatPct(r.percentage)} % ${r.survey_title || ''}`).join(', ')}` : null,
                                                        ].filter(Boolean).join(' | ') || undefined}
                                                    >
                                                        <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                        Partiel ({getExcludedSurveyIds(line).length})
                                                    </span>
                                                ) : line.on_hold ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                                                        En attente
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                                        OK
                                                    </span>
                                                )}
                                            </td>
                                            {!isAdminViewer && !(batch.creation_mode === 'express' && user?.role === 'chef_terrain') && !(batch.creation_mode === 'auto' && getUserSection() && batch.section_validation?.[getUserSection()]?.validated) && ((batch.canEdit && !(batch.creation_mode === 'auto' && user?.role === 'chef_terrain_superieur')) || (batch.creation_mode === 'auto' && batch.status === 'draft' && getUserSection() && batch.section_validation?.[getUserSection()] && !batch.section_validation[getUserSection()].validated)) && (
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <div className="flex items-center space-x-2">
                                                        {!isExcluded && (
                                                            <button
                                                                onClick={() => handleAddAdjustment(line)}
                                                                className="text-blue-600 hover:text-blue-700"
                                                                title="Ajouter un ajustement"
                                                            >
                                                                <FontAwesomeIcon icon={faPlus} />
                                                            </button>
                                                        )}
                                                        {batch.canEdit && line.on_hold && (
                                                            <button
                                                                onClick={() => handleForcePayment(line)}
                                                                className="text-orange-600 hover:text-orange-700"
                                                                title="Forcer le paiement"
                                                            >
                                                                <FontAwesomeIcon icon={faCheck} />
                                                            </button>
                                                        )}
                                                        {/* Exclusion buttons */}
                                                        {canExclude() && !isExcluded && !line.on_hold && (
                                                            <button
                                                                onClick={() => openExcludeLineModal(line)}
                                                                className="text-amber-600 hover:text-amber-700"
                                                                title="Exclure du paiement"
                                                            >
                                                                <FontAwesomeIcon icon={faBan} />
                                                            </button>
                                                        )}
                                                        {canExclude() && canFreezeLine(line) && (
                                                            <button
                                                                onClick={() => openFreezeLineModal(line)}
                                                                className="text-sky-600 hover:text-sky-700"
                                                                title="Geler : la personne ne travaille plus. Le montant (ou la dette) ne reviendra plus dans les lots, sauf si elle retravaille ou si vous le dégelez."
                                                            >
                                                                <FontAwesomeIcon icon={faSnowflake} />
                                                            </button>
                                                        )}
                                                        {canExclude() && isExcluded && (
                                                            <button
                                                                onClick={() => handleReincludeLine(line)}
                                                                className="text-green-600 hover:text-green-700"
                                                                title={isFrozen ? 'Annuler le gel (réinclure dans le paiement)' : 'Réinclure dans le paiement'}
                                                            >
                                                                <FontAwesomeIcon icon={faUndoAlt} />
                                                            </button>
                                                        )}
                                                        {canExclude() && isPartialExcluded && !isExcluded && (
                                                            <button
                                                                onClick={() => handleReincludeLine(line)}
                                                                className="text-green-600 hover:text-green-700"
                                                                title="Réinclure les enquêtes exclues"
                                                            >
                                                                <FontAwesomeIcon icon={faUndoAlt} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {/* Per-survey exclusion (multi-survey lines) and retenue (F2F, any line) */}
                                                    {canExclude() && !isExcluded && (() => {
                                                        const surveys = getSurveysArray(line);
                                                        const fullyExcluded = getFullyExcludedSurveyIds(line);
                                                        const payableSurveys = surveys.filter(s => !fullyExcluded.includes(String(s.survey_id)));
                                                        const showExclude = payableSurveys.length > 1;
                                                        const showRetenue = canRetenue(line);
                                                        const hasRetenue = getRetenues(line).length > 0;
                                                        if (!showExclude && !showRetenue && !hasRetenue) return null;
                                                        return (
                                                            <div className="mt-1 space-y-0.5">
                                                                {surveys.map((survey) => {
                                                                    const sid = String(survey.survey_id);
                                                                    const surveyExcluded = fullyExcluded.includes(sid);
                                                                    const retenue = getRetenueForSurvey(line, sid);
                                                                    return (
                                                                        <div key={survey.survey_id} className="flex items-center justify-between text-[10px]">
                                                                            <span className={`truncate max-w-[100px] ${surveyExcluded ? 'line-through text-amber-500' : retenue ? 'text-yellow-700' : 'text-gray-500'}`}
                                                                                title={retenue ? `Retenue ${formatPct(retenue.percentage)} % : ${Math.abs(retenue.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD gardés en garantie` : undefined}>
                                                                                {survey.survey_title}{retenue ? ` · ${formatPct(retenue.percentage)} %` : ''}
                                                                            </span>
                                                                            {surveyExcluded || retenue ? (
                                                                                <button
                                                                                    onClick={() => handleReincludeLine(line, sid)}
                                                                                    className="text-green-500 hover:text-green-600 ml-1"
                                                                                    title={retenue ? 'Annuler la retenue' : 'Réinclure cette enquête'}
                                                                                >
                                                                                    <FontAwesomeIcon icon={faUndoAlt} className="text-[9px]" />
                                                                                </button>
                                                                            ) : (
                                                                                <span className="flex items-center gap-1 ml-1">
                                                                                    {showRetenue && surveyHasWork(survey) && (
                                                                                        <button
                                                                                            onClick={() => openRetenueModal(line, sid)}
                                                                                            className="text-yellow-600 hover:text-yellow-700"
                                                                                            title="Retenue de garantie : retenir un pourcentage du travail de la semaine sur cette enquête (gardé jusqu'à la fin du travail)"
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPercent} className="text-[9px]" />
                                                                                        </button>
                                                                                    )}
                                                                                    {showExclude && (
                                                                                        <button
                                                                                            onClick={() => openExcludeSurveyModal(line, sid)}
                                                                                            className="text-amber-500 hover:text-amber-600"
                                                                                            title="Exclure cette enquête"
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faBan} className="text-[9px]" />
                                                                                        </button>
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                            )}
                                        </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                            );
                        })()}
                    </div>
                </div>


                {/* Metadata */}
                <div className="mt-6 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations du Lot</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {batch.validated_by && (
                            <>
                                <div>
                                    <span className="text-gray-600">Validé par:</span>
                                    <span className="ml-2 font-medium">{batch.validated_by?.name}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Validé le:</span>
                                    <span className="ml-2 font-medium">
                                        {new Date(batch.validated_at).toLocaleString('fr-FR')}
                                    </span>
                                </div>
                            </>
                        )}
                        {batch.locked_by && (
                            <>
                                <div>
                                    <span className="text-gray-600">Verrouillé par:</span>
                                    <span className="ml-2 font-medium">{batch.locked_by?.name}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Verrouillé le:</span>
                                    <span className="ml-2 font-medium">
                                        {new Date(batch.locked_at).toLocaleString('fr-FR')}
                                    </span>
                                </div>
                            </>
                        )}
                        <div>
                            <span className="text-gray-600">Créé le:</span>
                            <span className="ml-2 font-medium">
                                {new Date(batch.created_at).toLocaleString('fr-FR')}
                            </span>
                        </div>
                    </div>
                    
                    {/* Surveys Information */}
                    {batch.surveys && batch.surveys.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                                Enquêtes Incluses ({batch.surveys.length})
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {batch.surveys.map((survey, idx) => {
                                    const surveyExcluded = isSurveyExcludedFromBatch(survey.id);
                                    const showExcludeBtn = canExclude() && isSurveyForUserSection(survey);
                                    return (
                                    <div key={idx} className={`rounded-lg p-3 border ${surveyExcluded ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className={`font-medium text-sm ${surveyExcluded ? 'text-amber-700 line-through' : 'text-gray-900'}`}>
                                                    {survey.title}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    ID LimeSurvey: {survey.limesurvey_id}
                                                </div>
                                                {surveyExcluded && (
                                                    <div className="text-xs text-amber-600 mt-1">
                                                        <FontAwesomeIcon icon={faBan} className="mr-1" />
                                                        Exclue du paiement
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ml-3 flex items-start space-x-2">
                                                <div className="text-right">
                                                    <div className={`font-semibold ${surveyExcluded ? 'text-amber-600' : 'text-green-600'}`}>
                                                        {survey.payment_type === 'per_day'
                                                            ? `${parseFloat(survey.price_per_day || survey.unit_price || 0).toFixed(2)} MAD`
                                                            : `${parseFloat(survey.price_per_complete || survey.unit_price || 0).toFixed(2)} MAD`
                                                        }
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {survey.payment_type === 'per_day' ? 'par jour' : 'par complétion'}
                                                    </div>
                                                </div>
                                                {showExcludeBtn && !surveyExcluded && (
                                                    <button
                                                        onClick={() => openBulkExcludeSurveyModal(survey)}
                                                        className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 hover:text-amber-700 flex items-center justify-center transition-colors"
                                                        title="Exclure cette enquête de toutes les lignes"
                                                    >
                                                        <FontAwesomeIcon icon={faBan} className="text-sm" />
                                                    </button>
                                                )}
                                                {showExcludeBtn && surveyExcluded && (
                                                    <button
                                                        onClick={() => handleBulkReincludeSurvey(survey)}
                                                        className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700 flex items-center justify-center transition-colors"
                                                        title="Réinclure cette enquête dans toutes les lignes"
                                                    >
                                                        <FontAwesomeIcon icon={faUndoAlt} className="text-sm" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Adjustment Modal */}
            {showAdjustmentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">
                                Ajouter un Ajustement
                            </h3>
                            <button
                                onClick={() => setShowAdjustmentModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmitAdjustment}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type
                                </label>
                                <select
                                    value={adjustmentForm.type}
                                    onChange={(e) => setAdjustmentForm({...adjustmentForm, type: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                    required
                                >
                                    <option value="bonus">Prime / Bonus</option>
                                    <option value="penalty">Pénalité</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Enquête concernée
                                </label>
                                <SearchableSelect
                                    options={allSurveys}
                                    selectedValue={adjustmentForm.survey_id ? Number(adjustmentForm.survey_id) : null}
                                    onChange={(value) => setAdjustmentForm({...adjustmentForm, survey_id: value || ''})}
                                    placeholder="-- Sélectionner une enquête --"
                                    searchPlaceholder="Rechercher une enquête..."
                                    loading={loadingSurveys}
                                    getOptionLabel={(option) => option.title || `Enquête #${option.id}`}
                                    getOptionMeta={(option) => option.client?.name || (option.limesurvey_id ? `LS: ${option.limesurvey_id}` : null)}
                                    triggerClassName="px-4 py-2"
                                />
                                {!adjustmentForm.survey_id && (
                                    <input type="text" required value="" onChange={() => {}} className="sr-only" tabIndex={-1} />
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Montant (MAD)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={adjustmentForm.amount}
                                    onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                    required
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Raison
                                </label>
                                <textarea
                                    value={adjustmentForm.reason}
                                    onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                    required
                                    rows="3"
                                    placeholder="Expliquez la raison de cet ajustement..."
                                />
                            </div>
                            <div className="flex items-center justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAdjustmentModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    disabled={processing}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg disabled:opacity-50"
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                            Ajout...
                                        </>
                                    ) : (
                                        'Ajouter'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Exclusion Modal */}
            {showExclusionModal && exclusionTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className={`flex items-center justify-between p-6 border-b rounded-t-xl ${['freeze', 'retenue'].includes(exclusionTarget.mode) ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'}`}>
                            <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${exclusionTarget.mode === 'freeze' ? 'bg-sky-100' : exclusionTarget.mode === 'retenue' ? 'bg-yellow-100' : 'bg-amber-100'}`}>
                                    <FontAwesomeIcon
                                        icon={exclusionTarget.mode === 'freeze' ? faSnowflake : exclusionTarget.mode === 'retenue' ? faPercent : faBan}
                                        className={exclusionTarget.mode === 'freeze' ? 'text-sky-600' : exclusionTarget.mode === 'retenue' ? 'text-yellow-700' : 'text-amber-600'}
                                    />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {exclusionTarget.mode === 'freeze' ? 'Geler' : exclusionTarget.mode === 'retenue' ? 'Retenue de garantie' : exclusionTarget.surveyId ? 'Exclure une enquête' : 'Exclure du paiement'}
                                    </h3>
                                    <p className="text-sm text-gray-600">{exclusionTarget.line.person_name}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setShowExclusionModal(false); setExclusionTarget(null); }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            {exclusionTarget.mode === 'freeze' ? (
                                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-800 space-y-1">
                                    <div>
                                        
                                        {parseFloat(exclusionTarget.line.net || 0) < 0
                                            ? <>La dette de <strong>{Math.abs(parseFloat(exclusionTarget.line.net)).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</strong> sera gelée.</>
                                            : <>Le montant de <strong>{parseFloat(exclusionTarget.line.net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</strong> sera gelé.</>}
                                        {' '}Il ne reviendra plus dans les prochains lots.
                                    </div>
                                    <div>Le gel devient effectif à la confirmation du lot (vous pouvez l'annuler d'ici là en réintégrant la ligne).</div>
                                    <div>Il revient automatiquement si la personne retravaille, ou si vous le dégelez depuis la page des paiements non effectués.</div>
                                </div>
                            ) : exclusionTarget.mode === 'retenue' ? (() => {
                                const { survey, base, pct, valid, withheld, lineNet, exceedsNet } = getRetenuePreview(exclusionTarget, retenuePercent);
                                const fmt = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 space-y-1">
                                        <div>Montant de la semaine sur cette enquête (base de la retenue) : <strong>{fmt(base)} MAD</strong>
                                            {parseFloat(survey?.carryover_amount || 0) - parseFloat(survey?.retenue_release_amount || 0) > 0.01 ? ` (dont ${fmt(parseFloat(survey.carryover_amount) - parseFloat(survey?.retenue_release_amount || 0))} MAD reportés)` : ''}.</div>
                                        {parseFloat(survey?.retenue_release_amount || 0) > 0 && (
                                            <div className="text-xs">La garantie libérée ({fmt(parseFloat(survey.retenue_release_amount))} MAD) n'entre pas dans la base : elle est payée en entier.</div>
                                        )}
                                        {Math.abs(lineNet - base) > 0.01 && (
                                            <div>Montant net de la ligne : <strong>{fmt(lineNet)} MAD</strong> (autres enquêtes, déductions…).</div>
                                        )}
                                        {valid
                                            ? <div>Retenue de {formatPct(pct)} % : <strong>{fmt(withheld)} MAD</strong> retenus, <strong>{fmt(base - withheld)} MAD</strong> de l'enquête payés maintenant.</div>
                                            : <div>Indiquez le pourcentage à retenir (1 à 99 %). 100 % revient à exclure l'enquête.</div>}
                                        {exceedsNet && (
                                            <div className="text-red-600 font-medium">
                                                Impossible : {fmt(withheld)} MAD dépasse le montant net de la ligne ({fmt(lineNet)} MAD). Réduisez le pourcentage (maximum {formatPct(Math.floor((lineNet / base) * 10000) / 100)} %).
                                            </div>
                                        )}
                                        <div>Le montant retenu est gardé en garantie : il sera payé quand le chef terrain marquera le travail comme terminé (Paiements non effectués → Retenues de garantie).</div>
                                    </div>
                                );
                            })() : (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                {exclusionTarget.surveyId
                                    ? 'Le montant de cette enquête sera reporté au prochain lot de paiement.'
                                    : 'Le montant total de cette ligne sera reporté au prochain lot de paiement.'
                                }
                            </div>
                            )}
                            {exclusionTarget.surveyId && (() => {
                                const surveys = getSurveysArray(exclusionTarget.line);
                                const survey = surveys.find(s => String(s.survey_id) === String(exclusionTarget.surveyId));
                                return survey ? (
                                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                        <span className="text-gray-600">Enquête: </span>
                                        <span className="font-medium text-gray-900">{survey.survey_title}</span>
                                    </div>
                                ) : null;
                            })()}
                            {exclusionTarget.mode === 'retenue' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Pourcentage retenu <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative w-40">
                                        <input
                                            type="number"
                                            min="1"
                                            max="100"
                                            step="0.01"
                                            value={retenuePercent}
                                            onChange={(e) => setRetenuePercent(e.target.value)}
                                            className="w-full pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                            placeholder="Ex. 20"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {exclusionTarget.mode === 'freeze' ? 'Motif du gel' : exclusionTarget.mode === 'retenue' ? 'Motif de la retenue' : 'Motif de l\'exclusion'} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={exclusionReason}
                                    onChange={(e) => setExclusionReason(e.target.value)}
                                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 ${exclusionTarget.mode === 'freeze' ? 'focus:ring-sky-500 focus:border-sky-500' : exclusionTarget.mode === 'retenue' ? 'focus:ring-yellow-500 focus:border-yellow-500' : 'focus:ring-amber-500 focus:border-amber-500'}`}
                                    required
                                    rows="3"
                                    placeholder={exclusionTarget.mode === 'freeze'
                                        ? 'Ex. : a quitté, ne travaille plus ; a renoncé à son montant...'
                                        : exclusionTarget.mode === 'retenue'
                                        ? 'Ex. : retenue de garantie en attente du contrôle qualité...'
                                        : 'Expliquez pourquoi cette ligne doit être exclue du paiement...'}
                                />
                            </div>
                            {exclusionModalError && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                    {exclusionModalError}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={() => { setShowExclusionModal(false); setExclusionTarget(null); }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                disabled={exclusionProcessing}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleExcludeLine}
                                className={`${exclusionTarget.mode === 'freeze' ? 'bg-sky-600 hover:bg-sky-700' : exclusionTarget.mode === 'retenue' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-amber-600 hover:bg-amber-700'} text-white px-6 py-2 rounded-lg disabled:opacity-50`}
                                disabled={exclusionProcessing || !exclusionReason.trim() || (exclusionTarget.mode === 'retenue' && (!(parseFloat(String(retenuePercent).replace(',', '.')) >= 1) || getRetenuePreview(exclusionTarget, retenuePercent)?.exceedsNet))}
                            >
                                {exclusionProcessing ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        {exclusionTarget.mode === 'freeze' ? 'Gel...' : exclusionTarget.mode === 'retenue' ? 'Retenue...' : 'Exclusion...'}
                                    </>
                                ) : (
                                    exclusionTarget.mode === 'freeze' ? 'Geler' : exclusionTarget.mode === 'retenue' ? 'Appliquer la retenue' : 'Exclure'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Retenue Modal (F2F) */}
            {/* Garantie cumulée: each retenue held for this supervisor (information only, not in the net) */}
            {heldDetailLine && (() => {
                const fmtMad = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const held = Object.entries(heldDetailLine.retenue_held || {});
                const total = held.reduce((s, [, h]) => s + parseFloat(h.amount || 0), 0);
                const surveyTitle = (sid) => getSurveysArray(heldDetailLine).find(s => String(s.survey_id) === String(sid))?.survey_title
                    || batch?.surveys?.find(s => String(s.id) === String(sid))?.title || `Enquête #${sid}`;
                return (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setHeldDetailLine(null); }}>
                        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                            <div className="flex items-center justify-between p-6 border-b border-yellow-200 bg-yellow-50 rounded-t-xl">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                        <FontAwesomeIcon icon={faPercent} className="text-yellow-700" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Garantie cumulée : {fmtMad(total)} MAD</h3>
                                        <p className="text-sm text-gray-600">{heldDetailLine.person_name} · Ref: {heldDetailLine.person_ref || 'N/A'}</p>
                                    </div>
                                </div>
                                <button onClick={() => setHeldDetailLine(null)} className="text-gray-400 hover:text-gray-600">
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                            <div className="p-6 space-y-5 overflow-y-auto">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                                    Retenues des lots {batch?.status === 'confirmed' ? 'précédents' : 'déjà confirmés'}, gardées en garantie : elles ne sont pas dans le net de cette ligne.
                                    Elles seront payées en totalité une fois que le travail affecté au prestataire sera terminé.
                                </div>
                                {held.map(([sid, h]) => (
                                    <div key={sid}>
                                        <div className="flex items-baseline justify-between mb-2">
                                            <h4 className="font-medium text-gray-900">{surveyTitle(sid)}</h4>
                                            <span className="text-sm text-gray-600">
                                                <strong className="text-yellow-700">{fmtMad(h.amount)} MAD</strong> · {Number(h.qty || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} complète(s) · {h.weeks} semaine{h.weeks > 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        {(h.rows || []).length > 0 && (
                                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                                <table className="min-w-full text-sm">
                                                    <thead className="bg-gray-50 text-gray-500 text-xs">
                                                        <tr>
                                                            <th className="text-left px-3 py-2">Lot</th>
                                                            <th className="text-left px-3 py-2">Semaine</th>
                                                            <th className="text-right px-3 py-2">%</th>
                                                            <th className="text-right px-3 py-2">Base</th>
                                                            <th className="text-right px-3 py-2">Retenu</th>
                                                            <th className="text-right px-3 py-2">Compl.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {h.rows.map((r, i) => (
                                                            <tr key={i}>
                                                                <td className="px-3 py-2">
                                                                    <button onClick={() => { setHeldDetailLine(null); navigate(`/payments/${r.batch_id}`); }} className="text-blue-600 hover:underline">#{r.batch_id}</button>
                                                                </td>
                                                                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.period_start && r.period_end
                                                                    ? `${new Date(r.period_start).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} – ${new Date(r.period_end).toLocaleDateString('fr-FR')}`
                                                                    : '—'}</td>
                                                                <td className="px-3 py-2 text-right">{formatPct(r.percentage)} %</td>
                                                                <td className="px-3 py-2 text-right">{fmtMad(r.base_amount)}</td>
                                                                <td className="px-3 py-2 text-right font-medium text-yellow-700">{fmtMad(r.amount)}</td>
                                                                <td className="px-3 py-2 text-right">{Number(r.qty || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end p-6 border-t border-gray-200">
                                <button onClick={() => setHeldDetailLine(null)} className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg">Fermer</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {showBulkRetenueModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 rounded-t-xl">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faPercent} className="text-yellow-700" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Retenue en masse</h3>
                                    <p className="text-sm text-gray-600">Lignes F2F de ce lot</p>
                                </div>
                            </div>
                            <button onClick={() => setShowBulkRetenueModal(false)} disabled={bulkRetenueProcessing} className="text-gray-400 hover:text-gray-600">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto">
                            {bulkRetenueResult ? (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800">
                                        <strong>{bulkRetenueResult.applied}</strong> retenue{bulkRetenueResult.applied > 1 ? 's' : ''} appliquée{bulkRetenueResult.applied > 1 ? 's' : ''}.
                                    </div>
                                    {bulkRetenueResult.skipped.length > 0 && (
                                        <div>
                                            <div className="font-medium text-gray-700 mb-1">{bulkRetenueResult.skipped.length} non appliquée{bulkRetenueResult.skipped.length > 1 ? 's' : ''} :</div>
                                            <ul className="list-disc pl-5 text-gray-600 space-y-0.5">
                                                {bulkRetenueResult.skipped.map((s, i) => <li key={i}>{s}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                                        Pour chaque enquête choisie, le pourcentage est retenu sur cette enquête dans toutes les lignes F2F qui la contiennent. Le montant retenu est gardé en garantie jusqu'à ce que le travail soit marqué terminé.
                                    </div>
                                    {(() => {
                                        // Search-and-add: only the chosen surveys are listed (a lot can contain many surveys).
                                        const all = getF2fBatchSurveys();
                                        const selectedIds = Object.keys(bulkRetenuePct);
                                        const selected = selectedIds.map(sid => all.find(s => s.sid === sid)).filter(Boolean);
                                        const q = bulkRetenueSearch.trim().toLowerCase();
                                        const matches = q ? all.filter(s => !selectedIds.includes(s.sid) && s.title.toLowerCase().includes(q)).slice(0, 8) : [];
                                        const pctOf = (sid) => parseFloat(String(bulkRetenuePct[sid] ?? '').replace(',', '.'));
                                        const estimate = selected.reduce((sum, s) => { const p = pctOf(s.sid); return sum + (p >= 1 && p < 100 ? s.base * p / 100 : 0); }, 0);
                                        const fmtMad = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                        return (
                                            <div className="space-y-3">
                                                <div className="relative">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Enquêtes concernées</label>
                                                    <div className="relative">
                                                        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                                        <input
                                                            value={bulkRetenueSearch}
                                                            onChange={(e) => setBulkRetenueSearch(e.target.value)}
                                                            placeholder={all.length ? `Ajouter une enquête (${all.length} avec du travail cette semaine)…` : 'Aucune enquête avec du travail cette semaine'}
                                                            disabled={!all.length}
                                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                                                        />
                                                    </div>
                                                    {q && (
                                                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                            {matches.length === 0 ? (
                                                                <div className="px-3 py-2 text-sm text-gray-500">Aucune enquête trouvée</div>
                                                            ) : matches.map(s => (
                                                                <button
                                                                    key={s.sid}
                                                                    type="button"
                                                                    onClick={() => { setBulkRetenuePct({ ...bulkRetenuePct, [s.sid]: bulkRetenueSamePct || '' }); setBulkRetenueSearch(''); }}
                                                                    className="w-full text-left px-3 py-2 hover:bg-yellow-50 text-sm"
                                                                >
                                                                    <div className="text-gray-900">{s.title}</div>
                                                                    <div className="text-xs text-gray-500">{s.lines} prestataire{s.lines > 1 ? 's' : ''} · {fmtMad(s.base)} MAD de travail cette semaine</div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {selected.length > 0 && (
                                                    <>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-700">Même % pour toutes :</span>
                                                            <div className="relative w-24">
                                                                <input
                                                                    type="number" min="1" max="99" step="0.01"
                                                                    value={bulkRetenueSamePct}
                                                                    onChange={(e) => setBulkRetenueSamePct(e.target.value)}
                                                                    className="w-full pl-3 pr-7 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm"
                                                                    placeholder="—"
                                                                />
                                                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setBulkRetenuePct(Object.fromEntries(selectedIds.map(sid => [sid, bulkRetenueSamePct])))}
                                                                disabled={!bulkRetenueSamePct}
                                                                className="text-sm px-3 py-1.5 rounded-lg border border-yellow-400 text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
                                                            >
                                                                Appliquer à toutes
                                                            </button>
                                                        </div>
                                                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                                            {selected.map(s => (
                                                                <div key={s.sid} className="flex items-center justify-between gap-3 px-3 py-2">
                                                                    <div className="min-w-0">
                                                                        <div className="text-sm text-gray-800 truncate">{s.title}</div>
                                                                        <div className="text-xs text-gray-500">{s.lines} prestataire{s.lines > 1 ? 's' : ''} · {fmtMad(s.base)} MAD</div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        <div className="relative w-24">
                                                                            <input
                                                                                type="number" min="1" max="99" step="0.01"
                                                                                value={bulkRetenuePct[s.sid] ?? ''}
                                                                                onChange={(e) => setBulkRetenuePct({ ...bulkRetenuePct, [s.sid]: e.target.value })}
                                                                                className="w-full pl-3 pr-7 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                                                                                placeholder="—"
                                                                            />
                                                                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => { const next = { ...bulkRetenuePct }; delete next[s.sid]; setBulkRetenuePct(next); }}
                                                                            className="text-gray-400 hover:text-red-600"
                                                                            title="Retirer"
                                                                        >
                                                                            <FontAwesomeIcon icon={faTimes} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {estimate > 0 && (
                                                            <div className="text-sm text-yellow-800">Estimation : environ <strong>{fmtMad(estimate)} MAD</strong> retenus au total (lignes dont le net le permet).</div>
                                                        )}
                                                    </>
                                                )}
                                                {Object.values(bulkRetenuePct).some(v => parseFloat(String(v).replace(',', '.')) >= 100) && (
                                                    <div className="text-xs text-red-600">100 % n'est pas une retenue : utilisez l'exclusion de l'enquête.</div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Motif de la retenue <span className="text-red-500">*</span></label>
                                        <textarea
                                            value={bulkRetenueReason}
                                            onChange={(e) => setBulkRetenueReason(e.target.value)}
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                            placeholder="Ex. : retenue de garantie en attente du contrôle qualité..."
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            {bulkRetenueResult ? (
                                <button onClick={() => setShowBulkRetenueModal(false)} className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg">Fermer</button>
                            ) : (
                                <>
                                    <button onClick={() => setShowBulkRetenueModal(false)} disabled={bulkRetenueProcessing} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
                                    <button
                                        onClick={handleBulkRetenue}
                                        disabled={bulkRetenueProcessing || !bulkRetenueReason.trim()
                                            || !Object.values(bulkRetenuePct).some(v => { const p = parseFloat(String(v).replace(',', '.')); return p >= 1 && p < 100; })
                                            || Object.values(bulkRetenuePct).some(v => parseFloat(String(v).replace(',', '.')) >= 100)}
                                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg disabled:opacity-50"
                                    >
                                        {bulkRetenueProcessing ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Application...</> : 'Appliquer'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Survey Exclusion Modal */}
            {showBulkExclusionModal && bulkExclusionSurvey && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className="flex items-center justify-between p-6 border-b border-amber-200 bg-amber-50 rounded-t-xl">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faBan} className="text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        Exclure une enquête
                                    </h3>
                                    <p className="text-sm text-gray-600">Toutes les lignes contenant cette enquête</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setShowBulkExclusionModal(false); setBulkExclusionSurvey(null); }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="font-medium text-gray-900">{bulkExclusionSurvey.title}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {bulkExclusionSurvey.payment_type === 'per_day'
                                        ? `${parseFloat(bulkExclusionSurvey.price_per_day || bulkExclusionSurvey.unit_price || 0).toFixed(2)} MAD par jour`
                                        : `${parseFloat(bulkExclusionSurvey.price_per_complete || bulkExclusionSurvey.unit_price || 0).toFixed(2)} MAD par complétion`
                                    }
                                </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                Cette action exclura cette enquête de <strong>toutes les lignes</strong> de votre section.
                                Les montants seront reportés au prochain lot de paiement.
                                <br /><br />
                                Les lignes avec uniquement cette enquête seront exclues entièrement.
                                Les lignes avec plusieurs enquêtes seront partiellement exclues.
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Motif de l'exclusion <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={bulkExclusionReason}
                                    onChange={(e) => setBulkExclusionReason(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    required
                                    rows="3"
                                    placeholder="Expliquez pourquoi cette enquête doit être exclue du paiement..."
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={() => { setShowBulkExclusionModal(false); setBulkExclusionSurvey(null); }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                disabled={bulkExclusionProcessing}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleBulkExcludeSurvey}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg disabled:opacity-50"
                                disabled={bulkExclusionProcessing || !bulkExclusionReason.trim()}
                            >
                                {bulkExclusionProcessing ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        Exclusion...
                                    </>
                                ) : (
                                    'Exclure de toutes les lignes'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={() => {
                    setShowConfirmModal(false);
                    if (confirmAction && confirmAction.action) {
                        confirmAction.action();
                    }
                }}
                title={confirmAction?.title}
                message={confirmAction?.message}
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    setError('');
                }}
                message={successMessage}
            />

            {/* Full note viewer */}
            {noteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...noteDismiss}>
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-lg font-semibold text-gray-900">Note</h3>
                            <button onClick={() => setNoteModal(null)} className="text-gray-400 hover:text-gray-600">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto text-sm text-gray-800 whitespace-pre-wrap break-words">
                            {noteModal}
                        </div>
                        <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
                            <button onClick={() => setNoteModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Adjustment Detail Modal */}
            <AdjustmentDetailModal
                isOpen={showAdjustmentDetailModal}
                onClose={() => {
                    setShowAdjustmentDetailModal(false);
                    setSelectedAdjustment(null);
                }}
                adjustment={selectedAdjustment}
            />

            {/* Montant Breakdown Modal */}
            {montantBreakdown && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...montantDismiss}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {montantBreakdown._isController ? 'Detail du montant ajuste' : 'Detail du montant'}
                                </h3>
                                <p className="text-sm text-gray-500 mt-0.5">{montantBreakdown.survey_title}</p>
                            </div>
                            <button onClick={() => setMontantBreakdown(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FontAwesomeIcon icon={faTimes} className="text-lg" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Work amount */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium text-gray-700">
                                            {montantBreakdown._isController
                                                ? (montantBreakdown._isTask ? 'Montant des tâches' : 'Montant des contrôles')
                                                : 'Productivite du lot'}
                                        </div>
                                        {!montantBreakdown._isController && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {montantBreakdown.payment_type === 'per_day'
                                                    ? <>{Number(montantBreakdown.current_batch.productivity_completes).toLocaleString('fr-FR')} jour{montantBreakdown.current_batch.productivity_completes > 1 ? 's' : ''} x {Number(montantBreakdown.price_per_day).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD/jr</>
                                                    : <>{Number(montantBreakdown.current_batch.productivity_completes).toLocaleString('fr-FR')} complete{montantBreakdown.current_batch.productivity_completes > 1 ? 's' : ''} x {Number(montantBreakdown.price_per_complete).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD</>
                                                }
                                            </div>
                                        )}
                                        {montantBreakdown._isController && !montantBreakdown._isTask && montantBreakdown.current_batch.controls > 0 && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {montantBreakdown.current_batch.controls} controle{montantBreakdown.current_batch.controls > 1 ? 's' : ''} · {montantBreakdown.current_batch.days} jour{montantBreakdown.current_batch.days > 1 ? 's' : ''}
                                            </div>
                                        )}
                                        {montantBreakdown._isController && montantBreakdown._isTask && montantBreakdown.current_batch.days > 0 && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {montantBreakdown.current_batch.days} jour{montantBreakdown.current_batch.days > 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right font-semibold text-gray-900">
                                        {Number(montantBreakdown._isController
                                            ? montantBreakdown.current_batch.amount
                                            : montantBreakdown.current_batch.productivity_amount
                                        ).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                    </div>
                                </div>
                            </div>

                            {/* Carryover details */}
                            {montantBreakdown.current_batch.carryover_amount > 0 && (
                                <div className="space-y-2">
                                    <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        <FontAwesomeIcon icon={faUndoAlt} className="text-xs" />
                                        Reports
                                        {!montantBreakdown._isController && montantBreakdown.current_batch.carryover_qty > 0 && (
                                            <span>({montantBreakdown.current_batch.carryover_qty} {montantBreakdown.payment_type === 'per_day'
                                                ? (montantBreakdown.current_batch.carryover_qty > 1 ? 'jours' : 'jour')
                                                : `enquete${montantBreakdown.current_batch.carryover_qty > 1 ? 's' : ''}`
                                            })</span>
                                        )}
                                    </div>
                                    {montantBreakdown.current_batch.carryover_details && montantBreakdown.current_batch.carryover_details.length > 0 ? (
                                        montantBreakdown.current_batch.carryover_details.map((detail, idx) => (
                                            <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                                <div className="flex items-center justify-between">
                                                    <div className="text-sm text-gray-700">
                                                        {detail.from_batch_id ? `Report du lot #${detail.from_batch_id}` : 'Report'}
                                                    </div>
                                                    <div className="font-semibold text-gray-900">
                                                        +{Number(detail.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                                    </div>
                                                </div>
                                                {detail.person_name && (
                                                    <div className="text-xs text-gray-700 mt-1 font-medium">
                                                        {detail.person_name}
                                                        {detail.person_ref && <span className="text-gray-400 ml-1">({detail.person_ref})</span>}
                                                    </div>
                                                )}
                                                {detail.qty > 0 && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {detail.qty} {montantBreakdown.payment_type === 'per_day'
                                                            ? (detail.qty > 1 ? 'jours' : 'jour')
                                                            : `enquete${detail.qty > 1 ? 's' : ''}`
                                                        }
                                                    </div>
                                                )}
                                                {detail.reason && (
                                                    <div className="text-xs text-gray-600 mt-1 bg-gray-100 rounded px-2 py-1">
                                                        Motif: {detail.reason === 'unpaid_confirmation' ? 'Non paye confirme' : detail.reason === 'insufficient_amount' ? 'Montant insuffisant' : detail.reason === 'rejected' ? 'Annulé' : detail.reason}
                                                    </div>
                                                )}
                                                {detail.notes && (
                                                    <div className="text-xs text-gray-500 mt-1 italic">{detail.notes}</div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm text-gray-700">Report</div>
                                                <div className="font-semibold text-gray-900">
                                                    +{Number(montantBreakdown.current_batch.carryover_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Total */}
                            <div className="border-t-2 border-gray-300 pt-3">
                                <div className="flex items-center justify-between">
                                    <div className="font-bold text-gray-900">
                                        {montantBreakdown._isController ? 'Total Ajusté' : 'Total Montant'}
                                    </div>
                                    <div className="text-lg font-bold text-gray-900">
                                        {Number(montantBreakdown._isController
                                            ? montantBreakdown.current_batch.adjusted_amount
                                            : montantBreakdown.current_batch.amount
                                        ).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-5 border-t border-gray-200">
                            <button onClick={() => setMontantBreakdown(null)} className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium transition-colors">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excluded Breakdown Modal */}
            {excludedBreakdown && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...excludedDismiss}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Lignes exclues</h3>
                                <p className="text-sm text-gray-500 mt-0.5">{excludedBreakdown.survey_title}</p>
                            </div>
                            <button onClick={() => setExcludedBreakdown(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FontAwesomeIcon icon={faTimes} className="text-lg" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            {/* Summary */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                                <div className="text-sm text-amber-800 font-medium">
                                    {excludedBreakdown.payment_type === 'per_day'
                                        ? `${excludedBreakdown.current_batch.excluded_completes} jour${excludedBreakdown.current_batch.excluded_completes > 1 ? 's' : ''} exclu${excludedBreakdown.current_batch.excluded_completes > 1 ? 's' : ''}`
                                        : `${excludedBreakdown.current_batch.excluded_completes} complete${excludedBreakdown.current_batch.excluded_completes > 1 ? 's' : ''} exclu${excludedBreakdown.current_batch.excluded_completes > 1 ? 's' : ''}`
                                    }
                                </div>
                                <div className="font-semibold text-amber-900">
                                    {Number(excludedBreakdown.current_batch.excluded_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                </div>
                            </div>

                            {/* Detail per person */}
                            {excludedBreakdown.current_batch.excluded_details && excludedBreakdown.current_batch.excluded_details.length > 0 ? (
                                excludedBreakdown.current_batch.excluded_details.map((detail, idx) => (
                                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm font-medium text-gray-900">
                                                {detail.person_name}
                                                {detail.person_ref && <span className="text-gray-400 font-normal ml-1">({detail.person_ref})</span>}
                                            </div>
                                            <div className="text-sm font-semibold text-gray-700">
                                                -{Number(detail.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {detail.completes > 0 && <span>{detail.completes} {excludedBreakdown.payment_type === 'per_day' ? `jour${detail.completes > 1 ? 's' : ''}` : `complete${detail.completes > 1 ? 's' : ''}`} · </span>}
                                            {detail.type === 'full' ? 'Exclusion totale' : detail.type === 'retenue' ? `Retenue ${formatPct(detail.percentage)} %` : 'Exclusion partielle'}
                                        </div>
                                        {detail.reason && (
                                            <div className="text-xs text-gray-600 mt-1.5 bg-gray-100 rounded px-2 py-1">
                                                Motif: {detail.reason}
                                            </div>
                                        )}
                                        {(detail.excluded_by || detail.excluded_at) && (
                                            <div className="text-xs text-gray-400 mt-1">
                                                {detail.excluded_by && <span>Par {detail.excluded_by}</span>}
                                                {detail.excluded_by && detail.excluded_at && ' · '}
                                                {detail.excluded_at && <span>{detail.excluded_at}</span>}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-sm text-gray-500 italic">Pas de détail disponible</div>
                            )}
                        </div>
                        <div className="flex justify-end p-5 border-t border-gray-200">
                            <button onClick={() => setExcludedBreakdown(null)} className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium transition-colors">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Adjustment Details Modal */}
            {adjustmentBreakdown && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Détail des ajustements</h3>
                                <p className="text-sm text-gray-500 mt-0.5">{adjustmentBreakdown.survey_title}</p>
                            </div>
                            <button onClick={() => setAdjustmentBreakdown(null)} className="text-gray-400 hover:text-gray-600">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Base amount */}
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-700">
                                    {adjustmentBreakdown._isController
                                        ? (adjustmentBreakdown._isTask ? 'Montant des tâches' : 'Montant des contrôles')
                                        : 'Montant de productivité'}
                                </div>
                                <div className="font-semibold text-gray-900">
                                    {Number(adjustmentBreakdown.current_batch.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                </div>
                            </div>

                            {/* Adjustment entries */}
                            {adjustmentBreakdown.current_batch.adjustment_details && adjustmentBreakdown.current_batch.adjustment_details.length > 0 ? (
                                <div className="space-y-2">
                                    <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
                                        Ajustements
                                    </div>
                                    {adjustmentBreakdown.current_batch.adjustment_details.map((adj, idx) => (
                                        <div key={idx} className={`border rounded-lg p-3 ${adj.kind === 'retenue' ? 'bg-yellow-50 border-yellow-200' : adj.amount > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {adj.kind === 'retenue' ? `Retenue de garantie ${formatPct(adj.percentage)} %` : adj.type === 'bonus' ? 'Bonus' : adj.type === 'penalty' ? 'Pénalité' : adj.type === 'deleted_survey_deduction' ? 'Déduction' : adj.type === 'deleted_paid_submission_clawback' ? 'Récupération (soumission supprimée)' : adj.type === 'carryover' ? (adj.carryover_from_batch ? `Report du lot #${adj.carryover_from_batch}` : adj.from_batch_id ? `Report du lot #${adj.from_batch_id}` : 'Report') : adj.type === 'advance_frais_deplacement' ? 'Frais de déplacement' : adj.type === 'survey_exclusion' ? 'Exclusion enquête' : adj.type}
                                                </div>
                                                <div className={`font-semibold ${adj.kind === 'retenue' ? 'text-yellow-700' : adj.amount > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                                    {adj.amount > 0 ? '+' : ''}{Number(adj.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                                </div>
                                            </div>
                                            {adj.person_name && (
                                                <div className="text-xs text-gray-700 mt-1 font-medium">
                                                    {adj.person_name}
                                                    {adj.person_ref && <span className="text-gray-400 ml-1">({adj.person_ref})</span>}
                                                </div>
                                            )}
                                            {adj.reason && adj.type !== 'carryover' && (
                                                <div className="text-xs text-gray-600 mt-1 bg-white bg-opacity-60 rounded px-2 py-1">
                                                    {adj.reason}
                                                </div>
                                            )}
                                            {adj.at && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {new Date(adj.at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 italic">Pas de détail disponible</div>
                            )}

                            {/* Carryover if any (only for non-controller, since controller carryover is in adjustment_details) */}
                            {!adjustmentBreakdown._isController && adjustmentBreakdown.current_batch.carryover_amount > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                    <div className="font-medium text-gray-700">Reports</div>
                                    <div className="font-semibold text-gray-900">
                                        +{Number(adjustmentBreakdown.current_batch.carryover_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                    </div>
                                </div>
                            )}

                            {/* Total */}
                            <div className="border-t-2 border-gray-300 pt-3">
                                <div className="flex items-center justify-between">
                                    <div className="font-bold text-gray-900">Total Ajusté</div>
                                    <div className={`text-lg font-bold ${adjustmentBreakdown.current_batch.adjusted_amount > adjustmentBreakdown.current_batch.amount ? 'text-green-700' : 'text-red-700'}`}>
                                        {Number(adjustmentBreakdown.current_batch.adjusted_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} MAD
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-5 border-t border-gray-200">
                            <button onClick={() => setAdjustmentBreakdown(null)} className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium transition-colors">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Interviewer Breakdown Modal (F2F supervisor lines) */}
            {interviewerBreakdownLine && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...interviewerBreakdownDismiss}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Détail par enquêteur</h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {interviewerBreakdownLine.person_name || `Superviseur #${interviewerBreakdownLine.person_id}`}
                                </p>
                                {batch?.period_start && batch?.period_end && (
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {new Date(batch.period_start).toLocaleDateString('fr-FR')} - {new Date(batch.period_end).toLocaleDateString('fr-FR')}
                                    </p>
                                )}
                            </div>
                            <button onClick={() => setInterviewerBreakdownLine(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FontAwesomeIcon icon={faTimes} className="text-lg" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {getSurveysArray(interviewerBreakdownLine)
                                .filter(s => !s.is_carryover_only && (parseFloat(s.qty) || 0) > 0)
                                .map((survey, idx) => {
                                    const supervisorOwn = parseFloat(survey.supervisor_own_qty) || 0;
                                    const interviewers = survey.interviewers || [];
                                    return (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                                            <div className="font-medium text-gray-900 mb-3 text-sm">
                                                {survey.survey_title || `Enquête #${survey.survey_id}`}
                                            </div>
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-xs text-gray-500 uppercase">
                                                        <th className="text-left pb-2">Nom</th>
                                                        <th className="text-right pb-2">Complétions</th>
                                                        <th className="text-right pb-2">Montant</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {supervisorOwn > 0 && (
                                                        <tr>
                                                            <td className="py-1.5">
                                                                <span className="font-medium text-gray-800">
                                                                    {interviewerBreakdownLine.person_name || 'Superviseur'}
                                                                </span>
                                                                <span className="ml-1.5 text-xs text-red-600 font-medium">(prestataire)</span>
                                                            </td>
                                                            <td className="text-right py-1.5 font-medium">{supervisorOwn}</td>
                                                            <td className="text-right py-1.5 text-gray-600">
                                                                {(supervisorOwn * (parseFloat(survey.unit_price) || 0)).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {interviewers.map((int, i) => (
                                                        <tr key={i}>
                                                            <td className="py-1.5">
                                                                <span className="text-gray-800">{int.name}</span>
                                                                {int.ref && <span className="ml-1.5 text-xs text-gray-400">{int.ref}</span>}
                                                            </td>
                                                            <td className="text-right py-1.5 font-medium">{int.qty}</td>
                                                            <td className="text-right py-1.5 text-gray-600">
                                                                {(parseFloat(int.amount) || 0).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="border-t-2 border-gray-300">
                                                        <td className="pt-2 font-semibold text-gray-900">Total</td>
                                                        <td className="text-right pt-2 font-semibold">{parseFloat(survey.qty) || 0}</td>
                                                        <td className="text-right pt-2 font-semibold">
                                                            {(parseFloat(survey.amount) || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    );
                                })}

                            {/* F2F controlled-payment panel (only on new batches) */}
                            {interviewerBreakdownLine.detail?.uses_submission_control && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="font-semibold text-gray-900 mb-1 text-sm">Paiement selon contrôle</div>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Les soumissions contrôlées sont payées automatiquement. Choisissez combien de soumissions
                                        non contrôlées payer ; le reste est reporté au prochain lot.
                                    </p>
                                    {getSurveysArray(interviewerBreakdownLine).filter(s => s.control).length === 0 && (
                                        <div className="text-xs text-gray-500">Aucune soumission à traiter pour ce superviseur.</div>
                                    )}
                                    {getSurveysArray(interviewerBreakdownLine).filter(s => s.control).map((survey, idx) => {
                                        const c = survey.control || {};
                                        // Only the F2F section head (chef_terrain) may edit; everyone else is read-only.
                                        const editable = batch?.status === 'draft' && user?.role === 'chef_terrain';
                                        const choiceKey = `${interviewerBreakdownLine.id}_${survey.survey_id}`;
                                        const savedChoice = c.uncontrolled_to_pay || 0;
                                        const draftChoice = (choiceKey in f2fChoiceInputs) ? f2fChoiceInputs[choiceKey] : savedChoice;
                                        const choiceDirty = draftChoice !== savedChoice;
                                        return (
                                            <div key={idx} className="bg-white rounded-md p-3 mb-2 border border-blue-100">
                                                <div className="text-xs font-medium text-gray-800 mb-1.5">{survey.survey_title || `Enquête #${survey.survey_id}`}</div>
                                                <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mb-2">
                                                    <div>Contrôlées: <span className="font-semibold text-green-700">{c.controlled || 0}</span></div>
                                                    <div>Non contrôlées: <span className="font-semibold text-amber-700">{c.uncontrolled || 0}</span></div>
                                                    <div>Reportées: <span className="font-semibold text-gray-700">{c.deferred || 0}</span></div>
                                                </div>
                                                {editable ? (
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-xs text-gray-600">Payer non contrôlées:</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={c.uncontrolled || 0}
                                                            value={draftChoice}
                                                            disabled={processing}
                                                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100"
                                                            onChange={(e) => {
                                                                const v = Math.max(0, Math.min(parseInt(e.target.value, 10) || 0, c.uncontrolled || 0));
                                                                setF2fChoiceInputs(prev => ({ ...prev, [choiceKey]: v }));
                                                            }}
                                                        />
                                                        <span className="text-xs text-gray-400">/ {c.uncontrolled || 0}</span>
                                                        <button
                                                            type="button"
                                                            disabled={!choiceDirty || processing}
                                                            onClick={() => handleSetF2fChoice(interviewerBreakdownLine, survey.survey_id, draftChoice)}
                                                            className="px-3 py-1 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            Confirmer
                                                        </button>
                                                    </div>
                                                ) : (
                                                    (c.uncontrolled_to_pay || 0) > 0 && (
                                                        <div className="text-xs text-gray-600">
                                                            Payées (non contrôlées): <span className="font-semibold">{c.uncontrolled_to_pay}</span> / {c.uncontrolled || 0}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end p-5 border-t border-gray-200">
                            <button onClick={() => setInterviewerBreakdownLine(null)} className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2 rounded-lg font-medium transition-colors">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Section Reject Modal (chef_terrain_superieur) */}
            {showSectionRejectModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faTimesCircle} className="mr-2 text-red-600" />
                                Rejeter la section {sectionRejectTarget === 'cati' ? 'CATI' : sectionRejectTarget === 'f2f' ? 'F2F' : 'Contrôleurs'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowSectionRejectModal(false);
                                    setSectionRejectTarget(null);
                                    setSectionRejectNotes('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-600 mb-4">
                                La validation de cette section sera annulée et le responsable sera notifié pour réviser à nouveau.
                            </p>

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Commentaire (optionnel)
                            </label>
                            <textarea
                                value={sectionRejectNotes}
                                onChange={(e) => setSectionRejectNotes(e.target.value)}
                                placeholder="Raison du rejet ou corrections necessaires..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                rows="4"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowSectionRejectModal(false);
                                    setSectionRejectTarget(null);
                                    setSectionRejectNotes('');
                                }}
                                className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                disabled={sectionProcessing}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSubmitSectionReject}
                                disabled={sectionProcessing}
                                className="px-6 py-2.5 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {sectionProcessing ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                ) : (
                                    <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                                )}
                                Rejeter
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Batch Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faUndo} className="mr-2 text-gray-600" />
                                Retourner le lot pour révision
                            </h3>
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectionNote('');
                                    setError('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-600 mb-4">
                                Ce lot sera retourné en mode brouillon et le responsable administratif sera notifié de vos commentaires.
                            </p>

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Note de révision (optionnel)
                            </label>
                            <textarea
                                value={rejectionNote}
                                onChange={(e) => setRejectionNote(e.target.value)}
                                placeholder="Raisons du retour et corrections nécessaires..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                rows="5"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectionNote('');
                                    setError('');
                                }}
                                className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                disabled={processing}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={processing}
                                className="px-6 py-2.5 text-white bg-gray-800 hover:bg-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {processing ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                ) : (
                                    <FontAwesomeIcon icon={faUndo} className="mr-2" />
                                )}
                                Retourner pour révision
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentBatchDetail;

